// src/pages/StoryHub.js – Story Hub
// Updated: 2026-05-29 (Story Hub standard page format polish)
// Updated: 2026-05-29 – FIX: keep Story Hub edit cards fully visible and auto-save media cover uploads
// Updated: 2026-05-29 – POLISH: rectangular cover tiles for Story Hub cards
//
// What changed (UI/category text only):
// - Added protected visual-storytelling categories for phase 1
// - Added Phase 2 EN/FR safety notices for Young Illustrators, Comics, and Originals
// - Added EN/FR translation layer for Story Hub
// - Kept backend/storage logic intact
// - Forum and writing mode labels can display in French while values remain stable in English
// - No backend logic touched

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { useUser } from "../UserContext";
import { loadStoredStories, saveStoredStories, mergeStories } from "../storyStorage";
import { api } from "../api";
import { getPublicationInfo, isPublishedBook } from "../utils/publicationStatus";
import ProjectBreadcrumbTrail from "../components/ProjectBreadcrumbTrail";
import StoryTeaserEditor from "../components/StoryTeaserEditor";

const WRITING_MODES = ["Solo", "Collaborative", "Group (Open)", "Group (Closed)"];

const STORY_VISIBILITY_OPTIONS = [
  "private_draft",
  "nextscenes_readers",
  "public_preview",
  "published",
];

function normalizeStoryVisibilityStatus(value, publicationStatusValue = "") {
  const raw = String(value || "").trim();
  const compact = raw.toLowerCase().replace(/[\s-]+/g, "_");
  const pub = String(publicationStatusValue || "").trim().toLowerCase();

  if (compact === "private" || compact === "private_draft" || compact === "hidden") return "private_draft";
  if (
    compact === "nextscenes_readers" ||
    compact === "nextscenes_readers_only" ||
    compact === "readers" ||
    compact === "readers_only" ||
    compact === "registered" ||
    compact === "logged_in" ||
    compact === "logged_in_readers"
  ) return "nextscenes_readers";
  if (compact === "published" || compact === "published_completed" || compact === "published_book") return "published";
  if (pub === "publishedamazon" || pub === "publishedexternal" || pub === "published") return "published";
  if (compact === "public" || compact === "public_preview" || compact === "preview") return "public_preview";
  return "public_preview";
}

function legacyVisibilityFromStatus(status) {
  const normalized = normalizeStoryVisibilityStatus(status);
  if (normalized === "private_draft") return "private";
  if (normalized === "nextscenes_readers") return "readers";
  return "public";
}

function getStoryVisibilityStatus(story) {
  return normalizeStoryVisibilityStatus(
    story?.visibilityStatus || story?.readerVisibility || story?.storyVisibility || story?.visibility,
    story?.publicationStatus || story?.publishingStatus
  );
}

function getStoryVisibilityCopy(status, lang = "en") {
  const isFR = String(lang || "en").toLowerCase().startsWith("fr");
  const normalized = normalizeStoryVisibilityStatus(status);

  const copy = {
    private_draft: {
      en: { label: "Private Draft", help: "Only the author and admin can see this storyline." },
      fr: { label: "Brouillon privé", help: "Seuls l’auteur et l’admin peuvent voir cette storyline." },
    },
    nextscenes_readers: {
      en: { label: "NextScenes Readers Only", help: "Logged-in readers can read it; guests cannot." },
      fr: { label: "Lecteurs NextScenes seulement", help: "Les lecteurs connectés peuvent lire; les invités ne peuvent pas." },
    },
    public_preview: {
      en: { label: "Public Preview", help: "Guests and logged-in readers can read the approved public preview." },
      fr: { label: "Aperçu public", help: "Les invités et lecteurs connectés peuvent lire l’aperçu public approuvé." },
    },
    published: {
      en: { label: "Published / Completed", help: "Public completed-book visibility for finished or published work." },
      fr: { label: "Publié / terminé", help: "Visibilité publique pour une œuvre terminée ou publiée." },
    },
  };

  return copy[normalized]?.[isFR ? "fr" : "en"] || copy.public_preview[isFR ? "fr" : "en"];
}

function VisibilityStatusChip({ status, lang }) {
  const normalized = normalizeStoryVisibilityStatus(status);
  const copy = getStoryVisibilityCopy(normalized, lang);
  return (
    <span className={`ns-visibility-chip is-${normalized.replace(/_/g, "-")}`} title={copy.help}>
      {copy.label}
    </span>
  );
}

const NEXTSCENES_FOUNDING_WRITERS_LINKS = {
  en: "https://www.nextscenes.org/founding-writers-pilot",
  fr: "https://www.nextscenes.org/fr/pilote-auteurs-fondateurs",
};

function getNextScenesFoundingWritersLink(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr")
    ? NEXTSCENES_FOUNDING_WRITERS_LINKS.fr
    : NEXTSCENES_FOUNDING_WRITERS_LINKS.en;
}

function FoundingWritersPilotCard({ lang, title, body, linkLabel }) {
  return (
    <div className="ns-founding-pilot-card" role="note">
      <div className="ns-founding-pilot-kicker">{title}</div>
      <p className="ns-founding-pilot-body">{body}</p>
      <a
        className="ns-founding-pilot-link"
        href={getNextScenesFoundingWritersLink(lang)}
        target="_blank"
        rel="noopener noreferrer"
      >
        {linkLabel} →
      </a>
    </div>
  );
}

const NEXTSCENES_POLICY_LINKS = {
  en: [
    { label: "Writer Rights", href: "https://www.nextscenes.org/writer-rights" },
    { label: "Contributor Policy", href: "https://www.nextscenes.org/contributor-policy" },
    { label: "Publication and Benefit-Sharing", href: "https://www.nextscenes.org/publication-benefit-sharing" },
    { label: "Plain-Language Terms", href: "https://www.nextscenes.org/plain-language-terms" },
  ],
  fr: [
    { label: "Droits des auteurs", href: "https://www.nextscenes.org/fr/droits-des-auteurs" },
    { label: "Politique des contributeurs", href: "https://www.nextscenes.org/fr/politique-contributeurs" },
    { label: "Publication et partage des bénéfices", href: "https://www.nextscenes.org/fr/publication-partage-benefices" },
    { label: "Conditions simples", href: "https://www.nextscenes.org/fr/conditions-simples" },
  ],
};

function getNextScenesPolicyLinks(lang) {
  return String(lang || "en").toLowerCase().startsWith("fr")
    ? NEXTSCENES_POLICY_LINKS.fr
    : NEXTSCENES_POLICY_LINKS.en;
}

function PolicyLinksCard({ lang, title, body, compact = false }) {
  const links = getNextScenesPolicyLinks(lang);

  return (
    <div className={compact ? "ns-policy-links-card ns-policy-links-card-compact" : "ns-policy-links-card"} role="note">
      <div className="ns-policy-links-title">{title}</div>
      {body ? <p className="ns-policy-links-body">{body}</p> : null}
      <div className="ns-policy-links-row">
        {links.map((item) => (
          <a
            key={item.href}
            className="ns-policy-link"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.label}
          </a>
        ))}
      </div>
    </div>
  );
}


const ALL_FORUMS = [
  "Action Forum",
  "Adventure Forum",
  "Children Forum",
  "Comedy Forum",
  "Crime Forum",
  "Fantasy Forum",
  "Historical Forum",
  "Inspirational Stories Forum",
  "Mystery Forum",
  "Poetry Forum",
  "Romance Forum",
  "Suspense / Thriller Forum",
  "Science Fiction Forum",
  "Faith & Inspiration Forum",
  "Humour Forum",
  "Non-Fiction Stories Forum",
  "Young Adult Forum",
  "Young Illustrators Gallery",
  "Comics & Illustrated Stories Forum",
  "NextScenes Originals",
  "Unclassified Forum",
];

const FORUM_LABELS_FR = {
  "Action Forum": "Forum d’action",
  "Adventure Forum": "Forum d’aventure",
  "Children Forum": "Forum jeunesse",
  "Comedy Forum": "Forum de comédie",
  "Crime Forum": "Forum policier",
  "Fantasy Forum": "Forum fantastique",
  "Historical Forum": "Forum historique",
  "Inspirational Stories Forum": "Forum des récits inspirants",
  "Mystery Forum": "Forum mystère",
  "Poetry Forum": "Forum poésie",
  "Romance Forum": "Forum romance",
  "Suspense / Thriller Forum": "Forum suspense / thriller",
  "Science Fiction Forum": "Forum science-fiction",
  "Faith & Inspiration Forum": "Forum foi et inspiration",
  "Humour Forum": "Forum humour",
  "Non-Fiction Stories Forum": "Forum récits non romanesques",
  "Young Adult Forum": "Forum jeunes adultes",
  "Young Illustrators Gallery": "Galerie des jeunes illustrateurs",
  "Comics & Illustrated Stories Forum": "Forum bandes dessinées et récits illustrés",
  "NextScenes Originals": "Créations originales NextScenes",
  "Unclassified Forum": "Forum non classé",
};

const WRITING_MODE_LABELS_FR = {
  Solo: "Solo",
  Collaborative: "Collaboratif",
  "Group (Open)": "Groupe (ouvert)",
  "Group (Closed)": "Groupe (fermé)",
};

function detectLanguage() {
  if (typeof window === "undefined") return "en";

  const candidates = [
    window.localStorage.getItem("ns.lang"),
    window.localStorage.getItem("lang"),
    window.localStorage.getItem("language"),
    window.localStorage.getItem("locale"),
    document.documentElement?.lang,
    navigator.language,
  ]
    .filter(Boolean)
    .map((v) => String(v).toLowerCase());

  const foundFr = candidates.some((v) => v.startsWith("fr"));
  return foundFr ? "fr" : "en";
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function getStoryCreatedAt(story) {
  if (!story) return null;
  if (story.createdAt) return story.createdAt;
  if (!Array.isArray(story.scenes) || story.scenes.length === 0) return null;

  let earliest = null;
  for (const s of story.scenes) {
    if (!s?.createdAt) continue;
    const t = new Date(s.createdAt).getTime();
    if (Number.isNaN(t)) continue;
    if (earliest === null || t < earliest) earliest = t;
  }
  return earliest ? new Date(earliest).toISOString() : null;
}

function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function normalizeIdValue(v) {
  if (!v) return null;

  if (typeof v === "string" || typeof v === "number") return String(v);

  if (typeof v === "object") {
    const oid =
      (typeof v.$oid === "string" && v.$oid) ||
      (typeof v.oid === "string" && v.oid) ||
      (typeof v.id === "string" && v.id) ||
      (typeof v._id === "string" && v._id);

    if (oid) return String(oid);

    if (typeof v.toHexString === "function") {
      try {
        const s = v.toHexString();
        if (s && typeof s === "string") return s;
      } catch {}
    }

    if (typeof v.toString === "function") {
      try {
        const s = v.toString();
        if (s && s !== "[object Object]") return String(s);
      } catch {}
    }
  }

  return null;
}

function getId(story) {
  if (!story) return null;
  return (
    normalizeIdValue(story._id) ||
    normalizeIdValue(story.id) ||
    normalizeIdValue(story.storyId) ||
    null
  );
}

function getInitials(text) {
  if (!text) return "?";
  const parts = String(text).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function normalizeForumKey(forum) {
  const f = String(forum || "")
    .trim()
    .toLowerCase()
    .replace(/\s+forum$/i, "")
    .replace(/&/g, "and");

  if (!f) return "unclassified";

  if (f.includes("suspense") || f.includes("thriller")) return "thriller";
  if (f.includes("science") || f.includes("sci")) return "scifi";
  if (f.includes("non") && f.includes("fiction")) return "nonfiction";
  if (f.includes("young") && f.includes("adult")) return "ya";
  if (f.includes("faith")) return "faith";
  if (f.includes("inspirational")) return "inspirational";
  if (f.includes("young illustrators") || (f.includes("illustrators") && f.includes("gallery"))) return "youngIllustrators";
  if (f.includes("comics") || f.includes("illustrated stories")) return "comics";
  if (f.includes("nextscenes originals") || f.includes("originals")) return "originals";
  if (f.includes("children")) return "children";
  if (f.includes("adventure")) return "adventure";
  if (f.includes("action")) return "action";
  if (f.includes("comedy")) return "comedy";
  if (f.includes("humour") || f.includes("humor")) return "humour";
  if (f.includes("crime")) return "crime";
  if (f.includes("mystery")) return "mystery";
  if (f.includes("fantasy")) return "fantasy";
  if (f.includes("historical")) return "historical";
  if (f.includes("poetry")) return "poetry";
  if (f.includes("romance")) return "romance";

  return "unclassified";
}

const FORUM_ACCENT = {
  action: "#0f766e",
  adventure: "#0284c7",
  children: "#0ea5e9",
  youngIllustrators: "#0891b2",
  comics: "#7c3aed",
  originals: "#b45309",
  comedy: "#16a34a",
  crime: "#111827",
  fantasy: "#312e81",
  historical: "#7c2d12",
  inspirational: "#0b703d",
  mystery: "#0f172a",
  poetry: "#0ea5e9",
  romance: "#be185d",
  thriller: "#1f2937",
  scifi: "#1d4ed8",
  faith: "#15803d",
  humour: "#059669",
  nonfiction: "#334155",
  ya: "#1f2937",
  unclassified: "#0b703d",
};

function getForumAccentColor(forum) {
  const key = normalizeForumKey(forum);
  return FORUM_ACCENT[key] || FORUM_ACCENT.unclassified;
}

function getSelectedForumNotice(forum, t) {
  const f = String(forum || "").trim();

  if (f === "Young Illustrators Gallery") {
    return {
      kind: "young-illustrators",
      title: t.youngIllustratorsSafetyTitle,
      body: t.youngIllustratorsSafetyBody,
      guardrail: t.youngIllustratorsSafetyGuardrail,
    };
  }

  if (f === "Comics & Illustrated Stories Forum") {
    return {
      kind: "comics",
      title: t.comicsSafetyTitle,
      body: t.comicsSafetyBody,
      guardrail: "",
    };
  }

  if (f === "NextScenes Originals") {
    return {
      kind: "originals",
      title: t.originalsSafetyTitle,
      body: t.originalsSafetyBody,
      guardrail: "",
    };
  }

  return null;
}

function ForumSafetyNotice({ forum, t }) {
  const notice = getSelectedForumNotice(forum, t);
  if (!notice) return null;

  const accent = getForumAccentColor(forum);

  return (
    <div
      className={`ns-trust-notice ns-forum-safety-notice ns-forum-safety-notice-${notice.kind}`}
      role="note"
      style={{
        marginTop: 10,
        padding: "12px 14px",
        borderRadius: 16,
        border: `1px solid ${accent}33`,
        background:
          "linear-gradient(180deg, rgba(255,253,247,0.98) 0%, rgba(248,244,235,0.92) 100%)",
        boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
      }}
    >
      <div
        className="ns-trust-notice-title"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          color: "#102033",
          fontWeight: 900,
          lineHeight: 1.25,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 10,
            height: 10,
            borderRadius: 999,
            background: accent,
            display: "inline-block",
            flexShrink: 0,
          }}
        />
        {notice.title}
      </div>

      <p
        className="ns-trust-notice-body"
        style={{
          marginTop: 7,
          marginBottom: 0,
          color: "rgba(15,23,42,0.78)",
          lineHeight: 1.55,
          fontSize: 13.5,
        }}
      >
        {notice.body}
      </p>

      {notice.guardrail ? (
        <div
          style={{
            marginTop: 9,
            paddingTop: 9,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            color: "#064e3b",
            fontWeight: 900,
            fontSize: 13,
            lineHeight: 1.35,
          }}
        >
          {notice.guardrail}
        </div>
      ) : null}
    </div>
  );
}

function getAuthorInfoFromStory(story) {
  const p = story?.authorProfile || story?.author || null;

  const authorId =
    safeStr(p?.id) ||
    safeStr(story?.authorId) ||
    safeStr(story?.createdById) ||
    safeStr(story?.ownerId) ||
    "";

  const authorUsername =
    safeStr(p?.username) ||
    safeStr(story?.authorUsername) ||
    safeStr(story?.createdBy) ||
    "";

  const authorName = safeStr(p?.name) || safeStr(story?.authorName) || "";

  const authorAvatarUrl =
    safeStr(p?.avatarUrl) ||
    safeStr(story?.authorAvatarUrl) ||
    safeStr(story?.authorAvatar) ||
    "";

  const legacyName = safeStr(story?.createdBy) || safeStr(story?.author) || "";

  const display =
    authorName || (authorUsername ? `@${authorUsername}` : "") || legacyName || "Unknown";

  const authorPhotoUrl = safeStr(story?.authorPhotoUrl) || "";
  const bestPhoto = authorPhotoUrl || authorAvatarUrl || "";

  return {
    authorId,
    authorUsername,
    authorName,
    authorAvatarUrl,
    authorPhotoUrl,
    bestPhoto,
    display,
  };
}

function getAuthorInfoFromUser(user, username) {
  const id = safeStr(user?.id) || safeStr(user?._id) || "";
  const uName = safeStr(user?.username) || safeStr(username) || "";
  const name = safeStr(user?.name) || "";
  const avatarUrl =
    safeStr(user?.authorPhotoUrl) ||
    safeStr(user?.avatarUrl) ||
    safeStr(user?.avatar) ||
    safeStr(user?.photoUrl) ||
    safeStr(user?.profile?.authorPhotoUrl) ||
    safeStr(user?.profile?.avatarUrl) ||
    safeStr(user?.profile?.photoUrl) ||
    "";
  const display = name || (uName ? `@${uName}` : "") || "Unknown";
  return { id, username: uName, name, avatarUrl, display };
}

function isGroupModeLabel(writingMode) {
  const wm = String(writingMode || "").toLowerCase();
  return wm.includes("group");
}

function parseInviteEmails(inviteEmailsText) {
  const raw = String(inviteEmailsText || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

  const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);

  const out = [];
  const seen = new Set();

  for (const e of raw) {
    const lower = e.toLowerCase();
    if (!isEmail(lower)) continue;
    if (seen.has(lower)) continue;
    seen.add(lower);
    out.push(lower);
  }

  return out;
}

function isGroupStory(story) {
  const wm = safeStr(story?.writingMode).toLowerCase();
  return wm.includes("group");
}

function getGroupMode(story) {
  const wm = safeStr(story?.writingMode).toLowerCase();
  if (wm.includes("closed")) return "closed";
  if (wm.includes("open")) return "open";
  const m = safeStr(story?.groupMode || story?.group?.mode).toLowerCase();
  return m === "closed" ? "closed" : "open";
}

function isInvitedGroupMember(story, userLike, usernameFallback) {
  if (!isGroupStory(story)) return true;

  const uid = safeStr(userLike?.id) || safeStr(userLike?._id) || "";
  const uname = String(
    safeStr(userLike?.username) || safeStr(usernameFallback) || ""
  )
    .trim()
    .toLowerCase();

  if (!uid && !uname) return false;

  const ownerId =
    safeStr(story?.authorId) ||
    safeStr(story?.createdById) ||
    safeStr(story?.ownerId) ||
    safeStr(story?.authorProfile?.id) ||
    "";

  if (uid && ownerId && uid === ownerId) return true;

  const ownerUsername = String(
    safeStr(story?.authorUsername) ||
      safeStr(story?.createdBy) ||
      safeStr(story?.author) ||
      ""
  )
    .trim()
    .toLowerCase();

  // Username-only owner fallback applies only to truly legacy owner records.
  if (!ownerId && uname && ownerUsername && uname === ownerUsername) return true;

  const members = Array.isArray(story?.group?.members) ? story.group.members : [];

  // Signed-in group members are identified by account ID, not display names.
  return Boolean(uid && members.some((m) => String(m?.userId || "").trim() === uid));
}

function UserAvatar({ name, photoUrl }) {
  const initials = getInitials(name);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt={name || "author"}
        style={{
          width: 32,
          height: 32,
          borderRadius: "999px",
          objectFit: "cover",
          border: "1px solid rgba(15,23,42,0.12)",
          flexShrink: 0,
        }}
        onError={(e) => {
          try {
            e.currentTarget.src = "";
          } catch {}
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: "999px",
        background: "#0b703d",
        color: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials}
    </div>
  );
}

function shortSnippet(text, max = 220) {
  const s = String(text || "").trim();
  if (!s) return "";
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function preserveLocalFields(merged, localStored, fields) {
  const localById = new Map();
  (localStored || []).forEach((s) => {
    const id = getId(s);
    if (!id) return;
    localById.set(String(id), s);
  });

  return (merged || []).map((s) => {
    const id = getId(s);
    if (!id) return s;

    const local = localById.get(String(id));
    if (!local) return s;

    let out = s;
    for (const f of fields) {
      const remoteVal = String(out?.[f] || "").trim();
      const localVal = String(local?.[f] || "").trim();
      if (!remoteVal && localVal) out = { ...out, [f]: localVal };
    }
    return out;
  });
}

function canCreateStorylines(role) {
  const r = String(role || "").toLowerCase();
  return r === "writer" || r === "moderator" || r === "power" || r === "admin";
}

function countWords(text) {
  const t = String(text || "").trim();
  if (!t) return 0;
  return t.split(/\s+/).filter(Boolean).length;
}


function normalizeKnownCoverTitle(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[’‘`´]/g, "'")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function getKnownStoryHubLocalCoverByTitle(story) {
  const title = normalizeKnownCoverTitle(story?.title);

  if (!title) return "";

  // King Jar Lingo / Le Conte du roi Jar Lingo
  // Important: use existing local files only.
  if (
    title.includes("le conte du roi jar lingo") ||
    title.includes("conte du roi jar lingo") ||
    title.includes("roi jar lingo") ||
    title.includes("d evel broda")
  ) {
    return "/images/covers/le-conte.jpg";
  }

  if (
    title.includes("the tale of king jar lingo") ||
    title.includes("king jar lingo") ||
    title.includes("jar lingo")
  ) {
    return "/images/covers/jar-lingo.jpg";
  }

  if (title.includes("didie")) {
    return "/images/covers/didie-cover.jpg";
  }

  if (
    title.includes("reflexions du voyageur") ||
    title.includes("reflexions d un voyageur") ||
    title.includes("reflexions d un voyageur")
  ) {
    return "/images/covers/reflexions-du-voyageur-fr.webp";
  }

  if (
    title.includes("reflections of the wayfarer") ||
    title.includes("reflections of a wayfarer") ||
    title.includes("wayfarer")
  ) {
    return "/images/covers/reflections-of-the-wayfarer-en.webp";
  }

  if (
    title.includes("ugo et son petit chien") ||
    title.includes("ugo et son chien")
  ) {
    return "/images/covers/ugo-et-son-petit-chien.webp";
  }

  if (title.includes("ugo and her pet dog")) {
    return "/images/covers/ugo-and-her-pet-dog.webp";
  }

  if (
    title.includes("ugo et la femme papillon") ||
    title.includes("femme papillon")
  ) {
    return "/images/covers/ugo-femme-papillon.webp";
  }

  if (
    title.includes("ugo and the butterfly woman") ||
    title.includes("butterfly woman")
  ) {
    return "/images/covers/ugo-butterfly-woman.webp";
  }

  if (
    title.includes("ugo et son amie awa") ||
    title.includes("ugo et son amie")
  ) {
    return "/images/covers/ugo-and-her-friend-awa.webp";
  }

  if (
    title.includes("ugo and her friend awa") ||
    title.includes("ugo and her friend")
  ) {
    return "/images/covers/ugo-and-her-friend-awa.webp";
  }

  return "";
}

function getStoryHubDefaultCover(story) {
  const storyType = String(story?.storyType || "").trim().toLowerCase();
  const language = String(story?.language || story?.lang || "").trim().toLowerCase();
  const isShort = storyType === "short";
  const isFr = language.startsWith("fr");

  if (isFr) {
    return isShort
      ? "/images/default-covers/nouvelle-fr.webp"
      : "/images/default-covers/recit-fr.webp";
  }

  return isShort
    ? "/images/default-covers/short-story-en.webp"
    : "/images/default-covers/storyline-en.webp";
}

function getStoryHubCoverUrl(story) {
  // A cover explicitly saved by the writer must always take precedence.
  // Title-based local covers remain useful only as legacy fallbacks.
  const uploaded =
    safeStr(story?.coverImageUrl) ||
    safeStr(story?.coverUrl) ||
    safeStr(story?.coverImage) ||
    safeStr(story?.imageUrl) ||
    safeStr(story?.cover) ||
    "";

  if (uploaded) return uploaded;

  const knownLocal = getKnownStoryHubLocalCoverByTitle(story);
  if (knownLocal) return knownLocal;

  return getStoryHubDefaultCover(story);
}

function clampWords(text, maxWords) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= maxWords) return String(text || "").trim();
  return words.slice(0, maxWords).join(" ");
}

function safeLocalGet(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeLocalSet(key, value) {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {}
}

function StoryIdentityTile({ story }) {
  const accent = getForumAccentColor(story?.forum);
  const title = safeStr(story?.title) || "Untitled";
  const initials = getInitials(title);
  const coverUrl = getStoryHubCoverUrl(story);
  const hasCover = Boolean(coverUrl);

  return (
    <div
      className={`ns-storyhub-coverTile ${hasCover ? "has-image" : "no-image"}`}
      title={title}
      style={{ "--story-accent": accent }}
    >
      {hasCover ? (
        <img
          src={coverUrl}
          alt={title}
          className="ns-storyhub-coverTileImage"
          onError={(e) => {
            try {
              e.currentTarget.style.display = "none";
              const tile = e.currentTarget.parentElement;
              if (tile) {
                tile.classList.remove("has-image");
                tile.classList.add("no-image");
              }
              const fallback = tile?.querySelector(".ns-storyhub-coverTileFallback");
              if (fallback) fallback.style.display = "grid";
            } catch {}
          }}
        />
      ) : null}

      <div
        className="ns-storyhub-coverTileFallback"
        style={{ display: hasCover ? "none" : "grid" }}
      >
        <span>{initials}</span>
      </div>
    </div>
  );
}

export default function StoryHub({
  onNavigatePage,
  onEnterWriterRoom,
  onOpenReaderView,
  lang = "en",
  projectHubContext = {},
}) {
  const userCtx = useUser() || {};
  const isAuthenticated = Boolean(userCtx.isAuthenticated);
  const user = userCtx.user || null;
  const username = safeStr(userCtx.username);
  const refreshMe = typeof userCtx.refreshMe === "function" ? userCtx.refreshMe : null;
  const upgradeToWriter = typeof userCtx.upgradeToWriter === "function" ? userCtx.upgradeToWriter : null;

  const role = user?.role || "guest";
  const isWriterOrAdmin = canCreateStorylines(role);

 

  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [title, setTitle] = useState("");
  const [forum, setForum] = useState("");
  const [description, setDescription] = useState("");
  const [synopsis, setSynopsis] = useState("");
  const [aboutAuthor, setAboutAuthor] = useState("");
  const [authorPhotoUrl, setAuthorPhotoUrl] = useState("");
  const [authorPhotoUploadFile, setAuthorPhotoUploadFile] = useState(null);
  const [authorPhotoUploading, setAuthorPhotoUploading] = useState(false);
  const [authorPhotoUploadMessage, setAuthorPhotoUploadMessage] = useState("");
  const [coverImageUrl, setCoverImageUrl] = useState("");
  const [coverUploadFile, setCoverUploadFile] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadMessage, setCoverUploadMessage] = useState("");
  const [writingMode, setWritingMode] = useState("Solo");
  const [storyType, setStoryType] = useState("longform");
  const [visibilityStatus, setVisibilityStatus] = useState("public_preview");

  

  const [inviteEmailsText, setInviteEmailsText] = useState("");
  const [inviteEmailsError, setInviteEmailsError] = useState("");

  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState("");

  const [writerAgreementOpen, setWriterAgreementOpen] = useState(false);
  const [writerAgreementChecked, setWriterAgreementChecked] = useState(false);
  const [writerUpgradeBusy, setWriterUpgradeBusy] = useState(false);
  const [writerUpgradeMessage, setWriterUpgradeMessage] = useState("");

  const [synopsisEditorOpenId, setSynopsisEditorOpenId] = useState(null);
  const [synopsisDraft, setSynopsisDraft] = useState("");
  const [synopsisSaveMsg, setSynopsisSaveMsg] = useState("");

  const [descriptionEditorOpenId, setDescriptionEditorOpenId] = useState(null);
  const [descriptionDraft, setDescriptionDraft] = useState("");
  const [descriptionSaveMsg, setDescriptionSaveMsg] = useState("");

  const [aboutEditorOpenId, setAboutEditorOpenId] = useState(null);
  const [aboutDraft, setAboutDraft] = useState("");
  const [aboutSaveMsg, setAboutSaveMsg] = useState("");

  const [mediaEditorOpenId, setMediaEditorOpenId] = useState(null);
  const [mediaAuthorPhotoDraft, setMediaAuthorPhotoDraft] = useState("");
  const [mediaCoverDraft, setMediaCoverDraft] = useState("");
  const [mediaCoverUploadFile, setMediaCoverUploadFile] = useState(null);
  const [mediaCoverUploading, setMediaCoverUploading] = useState(false);
  const [mediaCoverUploadMessage, setMediaCoverUploadMessage] = useState("");
  const [mediaSaveMsg, setMediaSaveMsg] = useState("");

  const [publicationEditorOpenId, setPublicationEditorOpenId] = useState(null);
  const [publicationDraft, setPublicationDraft] = useState({
    publicationStatus: "inDevelopment",
    publicationTitle: "",
    publicationSubtitle: "",
    publishedAuthorName: "",
    publicationUrl: "",
    publicationCoverUrl: "",
    publishedAt: "",
    publicationNote: "",
  });
  const [publicationSaveMsg, setPublicationSaveMsg] = useState("");

  const [visibilityEditorOpenId, setVisibilityEditorOpenId] = useState(null);
  const [visibilityDraft, setVisibilityDraft] = useState("public_preview");
  const [visibilitySaveMsg, setVisibilitySaveMsg] = useState("");


  const [focusStoryId, setFocusStoryId] = useState(null);
  const [storyHubTab, setStoryHubTab] = useState("overview");


  const t = useMemo(() => {
    const isFr = lang === "fr";

    return isFr
      ? {
          home: "ACCUEIL",
          storyHub: "STORY HUB",
          projectHubBreadcrumb: "HUB DE PROJETS",
          myProjectsBreadcrumb: "MES PROJETS",
          createNovelStoryBreadcrumb:
            "CRÉER UN NOUVEAU ROMAN / HISTOIRE",
          pageTitle: "Story Hub",
          pageSubtitleWriter:
            "Créez de nouvelles histoires et consultez les tableaux que vous possédez. Pour explorer toutes les histoires publiques, utilisez Storylines dans le menu du haut.",
          pageSubtitleUser:
            "Explorez le cœur de NextScenes. L’inscription, la lecture et la participation de base sont gratuites : vous pouvez lire, commenter, proposer des scènes et voter. Les writers peuvent créer des histoires et écrire le canon.",
          welcomeTitle: "Bienvenue dans Story Hub",
          welcomeP1:
            "C’est ici que les histoires sont créées et gérées. Considérez cela comme la table de préparation où chaque nouveau récit prend d’abord forme.",
          welcomeP2:
            "Quand une histoire est prête à passer de l’idée à la scène, continuez dans Writer Studio pour écrire le canon ou soumettre des propositions.",
          openWriterStudio: "Ouvrir Writer Studio",
          openReaderView: "Ouvrir Reader View",

          createStoryline: "Créer une nouvelle histoire",
          storyControlNoticeTitle: "Votre histoire reste sous votre contrôle",
          storyControlNoticeBody:
            "Lorsque vous créez une Storyline, vous choisissez son niveau d’ouverture ou de confidentialité. Seul le contenu approuvé devient partie du Canon officiel. Les contributeurs peuvent soumettre des propositions uniquement selon le mode d’écriture que vous choisissez.",
          policyLinksTitle: "Liens utiles",
          policyLinksBody:
            "Repères calmes sur les droits, les contributions, la publication et les conditions simples de NextScenes.",
          foundingWritersPilotTitle: "Pilote des auteurs fondateurs",
          foundingWritersPilotBody:
            "Un espace pour les auteurs qui portent une histoire originale et veulent la faire grandir avec sérieux, visibilité et engagement des lecteurs.",
          foundingWritersPilotLink: "Découvrir le pilote",
          guestBrowsing: "Vous naviguez comme invité. L’inscription est gratuite : connectez-vous pour créer ou participer davantage.",
          login: "Connexion",
          currentUserRole1: "Vous êtes actuellement",
          currentUserRole2:
            "Vous pouvez lire toutes les histoires, commenter, soumettre des propositions et voter pendant les phases de propositions.",
          currentUserRole3:
            "Pour créer des histoires et écrire des scènes canon, vous avez besoin du rôle Writer.",

          title: "Titre",
          titlePlaceholder: "Exemple : Le Dernier Train de Lagos",
          forumGenre: "Forum / Genre",
          selectForum: "Sélectionnez un forum…",
          youngIllustratorsSafetyTitle: "Galerie des jeunes illustrateurs : espace protégé",
          youngIllustratorsSafetyBody:
            "La Galerie des jeunes illustrateurs est un espace protégé pour les œuvres créées par des enfants et des jeunes. Les œuvres d’enfants doivent être soumises uniquement par un parent, un tuteur, un enseignant ou un adulte autorisé. N’indiquez pas le nom complet de l’enfant, son visage, le nom de son école, son adresse, son numéro de téléphone ou tout autre détail privé permettant de l’identifier.",
          youngIllustratorsSafetyGuardrail:
            "Les enfants créent. Les adultes soumettent. NextScenes modère.",
          comicsSafetyTitle: "Bandes dessinées et récits illustrés",
          comicsSafetyBody:
            "Pour les créateurs de bandes dessinées, les illustrateurs et les conteurs visuels. Cet espace est destiné aux récits illustrés, aux séries dessinées, aux planches, aux légendes et aux œuvres narratives visuelles.",
          originalsSafetyTitle: "Créations originales NextScenes",
          originalsSafetyBody:
            "Pour les projets sélectionnés ou créés par NextScenes, y compris les séries dessinées mises en avant, les expérimentations officielles et les œuvres créatives choisies.",
          writingMode: "Mode d’écriture",
          storyType: "Type d’histoire",
          storyTypeLongform: "Histoire longue",
          storyTypeShort: "Nouvelle",
          storyTypeHelp:
            "Choisissez “Nouvelle” pour un récit court et complet, ou “Histoire longue” pour une œuvre évolutive.",
          inviteEmails: "Emails des invités (séparés par des virgules)",
          invitePlaceholder: "Exemple : a@x.com, b@y.com",
          inviteExample: "Exemple : a@x.com, b@y.com",

          description: "Description",
          descriptionPlaceholder:
            "Décrivez brièvement la prémisse et le ton de cette histoire…",

          synopsis: "Synopsis (recommandé, max 150 mots)",
          synopsisPlaceholder:
            "Un court synopsis qui explique aux lecteurs de quoi parle cette œuvre.",
          words: "Mots",

          aboutAuthor: "À propos de l’auteur (max 150 mots)",
          aboutAuthorPlaceholder:
            "Brève bio d’auteur pour aider les lecteurs à faire confiance à la voix derrière l’œuvre.",

          authorPhotoUrl: "URL de la photo de l’auteur (optionnel)",
          authorPhotoPlaceholder: "https://.../photo-auteur.jpg",
          authorPhotoTip:
            "Conseil : utilisez un lien direct vers l’image (se terminant par .jpg/.png/.webp).",
          imageResponsibilityLine:
            "Utilisez uniquement des images que vous possédez ou que vous avez l’autorisation d’utiliser.",
          illustrationPolicyLabel: "Politique sur les illustrations",
          illustrationPolicyHref:
            "https://www.nextscenes.org/fr/politique-illustrations-televersements",

          coverImageUrl: "URL de l’image de couverture (optionnel)",
          coverImagePlaceholder: "https://.../cover.jpg",
          coverUploadLabel: "Téléverser une couverture",
          coverUploadButton: "Téléverser",
          coverUploading: "Téléversement…",
          coverUploadSuccess: "Couverture téléversée. L’URL a été ajoutée ci-dessous.",
          coverUploadMissing: "Veuillez choisir une image de couverture.",
          coverUploadInvalid: "Utilisez une image JPG, PNG ou WEBP de 5 Mo maximum.",
          coverUploadFailed: "Le téléversement de la couverture a échoué.",
          coverUploadHelp: "JPG, PNG ou WEBP, 5 Mo maximum.",

          creating: "Création…",
          createButton: "Créer l’histoire",

          yourStorylines: "Vos histoires",
          writerBoards: "Tableaux des writers",
          guestRight:
            "Vous naviguez actuellement en tant qu’invité. L’inscription est gratuite : connectez-vous pour commenter, proposer et voter.",
          noUsername:
            "Connecté, mais aucun nom d’utilisateur n’est défini. Définissez-en un pour que l’attribution soit correctement affichée.",
          loggedInAs: "Connecté en tant que",
          noStorylinesYet:
            "Vous n’avez pas encore créé d’histoires. Utilisez le formulaire à gauche pour commencer.",
          onlyWritersSeeOwned:
            "Seuls les Writers voient ici les tableaux qu’ils possèdent. Pour explorer les histoires publiques, utilisez Storylines dans le menu du haut.",

          by: "Par",
          unclassifiedForum: "Forum non classé",
          created: "Créé",
          scenes: "Scènes",

          max200Words: "max 200 mots",
          edit: "Modifier",
          add: "Ajouter",
          save: "Enregistrer",
          cancel: "Annuler",
          saveAboutAuthor: "Enregistrer la bio auteur",
          saveSynopsis: "Enregistrer le synopsis",
          saveImages: "Enregistrer les images",
          notProvidedYet: "Pas encore renseigné.",
          images: "Images",
          coverSet: "Couverture : définie",
          coverNotSet: "Couverture : non définie",
          authorPhotoSet: "Photo auteur : définie",
          authorPhotoNotSet: "Photo auteur : non définie",
          useDirectImageLinks: "Utilisez des liens directs vers les images.",

          enterWritersRoom: "Entrer dans la salle d’écriture",
          openReaderViewCard: "Ouvrir Reader View",

          loading: "Chargement…",
          unknown: "Inconnu",
          userLabel: "Utilisateur",
          guestLabel: "Invité",
          writerRoleNeeded: "Writer",
          groupWriterRestrictedAlert:
         "Vous ne faites pas partie de ce groupe d’écriture. Vous pouvez lire l’histoire, mais pas entrer dans la salle d’écriture.",
          alertWriterFallback:
           "Impossible d’ouvrir Writer Studio depuis ici.",
          alertReaderFallback:
           "Impossible d’ouvrir Reader View depuis ici.",
          descriptionPlaceholderShort:
            "Mettez à jour la description de cette histoire…",
          aboutAuthorTitle: "À propos de l’auteur",
        }

      : {
          home: "HOME",
          storyHub: "STORY HUB",
          projectHubBreadcrumb: "PROJECT HUB",
          myProjectsBreadcrumb: "MY PROJECTS",
          createNovelStoryBreadcrumb:
            "CREATE A NEW NOVEL / STORY",
          pageTitle: "Story Hub",
          pageSubtitleWriter:
            "Start new storylines and review the boards you own. To explore all public storylines, use Storylines in the top menu.",
          pageSubtitleUser:
            "Explore the heart of NextScenes. Registration, reading, and core participation are free: you can read, comment, propose scenes, and vote. Writers can create storylines and write canon.",
          welcomeTitle: "Welcome to Story Hub",
          welcomeP1:
            "This is where storylines are created and managed. Think of it as the planning table where each new narrative first takes shape.",
          welcomeP2:
            "When a storyline is ready to move from idea to scene, continue in Writer Studio to write canon or submit proposals.",
          openWriterStudio: "Open Writer Studio",
          openReaderView: "Open Reader View",

          createStoryline: "Create a New Storyline",
          storyControlNoticeTitle: "Your story remains under your control",
          storyControlNoticeBody:
            "When you create a Storyline, you choose how open or private it should be. Only approved material becomes part of the official Canon. Contributors may submit proposals only according to the writing mode you choose.",
          policyLinksTitle: "Useful policy links",
          policyLinksBody:
            "Quiet guidance on writer rights, contributor rules, publication, benefit-sharing, and simple terms.",
          foundingWritersPilotTitle: "Founding Writers Pilot",
          foundingWritersPilotBody:
            "A doorway for writers carrying original stories and ready to grow them with seriousness, visibility, and reader engagement.",
          foundingWritersPilotLink: "Learn about the pilot",
          guestBrowsing: "You are browsing as a Guest. Registration is free: log in to create or participate more fully.",
          login: "Login",
          currentUserRole1: "You are currently a",
          currentUserRole2:
            "You can read all storylines, comment, submit proposals, and vote during proposal rounds.",
          currentUserRole3:
            "To create storylines and write canon scenes, you need a Writer role.",

          title: "Title",
          titlePlaceholder: "Example: The Last Train from Lagos",
          forumGenre: "Forum / Genre",
          selectForum: "Select a forum…",
          youngIllustratorsSafetyTitle: "Young Illustrators Gallery: protected space",
          youngIllustratorsSafetyBody:
            "Young Illustrators Gallery is a protected space for artwork created by children and young people. Children’s artwork must be submitted only by a parent, guardian, teacher, or approved adult. Do not include a child’s full name, face, school name, address, phone number, or any private identifying detail.",
          youngIllustratorsSafetyGuardrail:
            "Children create. Adults submit. NextScenes moderates.",
          comicsSafetyTitle: "Comics & Illustrated Stories",
          comicsSafetyBody:
            "For comic creators, illustrators, and visual storytellers. This space is for illustrated storytelling, comic series, panels, captions, and visual narrative work.",
          originalsSafetyTitle: "NextScenes Originals",
          originalsSafetyBody:
            "For platform-curated or in-house story projects, including featured comic series, official experiments, and selected creative works.",
          writingMode: "Writing Mode",
          storyType: "Story Type",
          storyTypeLongform: "Long-form Story",
          storyTypeShort: "Short Story",
          storyTypeHelp:
           "Choose “Short Story” for a complete shorter work, or “Long-form Story” for an evolving larger work.",
          inviteEmails: "Invitee emails (comma separated)",
          invitePlaceholder: "Example: a@x.com, b@y.com",
          inviteExample: "Example: a@x.com, b@y.com",

          description: "Description",
          descriptionPlaceholder:
            "Briefly describe the premise and tone of this storyline…",

          synopsis: "Synopsis (recommended, max 150 words)",
          synopsisPlaceholder:
            "A short synopsis that tells readers what this work is about.",
          words: "Words",

          aboutAuthor: "About the Author (max 150 words)",
          aboutAuthorPlaceholder:
            "Brief author bio to help readers trust the voice behind the work.",

          authorPhotoUrl: "Author Photo URL (optional)",
          authorPhotoPlaceholder: "https://.../author-photo.jpg",
          authorPhotoTip:
            "Tip: use a direct image URL (ends with .jpg/.png/.webp).",
          authorPhotoUploadLabel: "Upload author photo",
          authorPhotoUploadHelp: "JPG, PNG or WEBP. Maximum 5 MB.",
          authorPhotoUploadButton: "Upload photo",
          authorPhotoUploading: "Uploading…",
          authorPhotoUploadMissing: "Choose an author photo first.",
          authorPhotoUploadInvalid: "Use a JPG, PNG or WEBP image, maximum 5 MB.",
          authorPhotoUploadSuccess: "Author photo uploaded. The URL has been added to the field.",
          authorPhotoUploadFailed: "Author photo upload failed.",
          imageResponsibilityLine:
            "Use only images you own or have permission to use.",
          illustrationPolicyLabel: "Illustration Upload Policy",
          illustrationPolicyHref:
            "https://www.nextscenes.org/illustration-upload-policy",

          coverImageUrl: "Book Cover Image URL (optional)",
          coverImagePlaceholder: "https://.../cover.jpg",
          coverUploadLabel: "Upload book cover",
          coverUploadButton: "Upload",
          coverUploading: "Uploading…",
          coverUploadSuccess: "Cover uploaded. The URL has been added below.",
          coverUploadMissing: "Please choose a cover image first.",
          coverUploadInvalid: "Use a JPG, PNG, or WEBP image up to 5 MB.",
          coverUploadFailed: "Cover upload failed.",
          coverUploadHelp: "JPG, PNG or WEBP, maximum 5 MB.",

          creating: "Creating…",
          createButton: "Create Storyline",

          yourStorylines: "Your Storylines",
          writerBoards: "Writer Boards",
          guestRight:
            "You are browsing as a Guest. Registration is free: log in to comment, propose, and vote.",
          noUsername:
            "Logged in, but no username is set. Set a username to ensure authorship is properly displayed.",
          loggedInAs: "Logged in as",
          noStorylinesYet:
            "You haven't created any storylines yet. Use the form on the left to start one.",
          onlyWritersSeeOwned:
            "Only Writers see “owned boards” here. To explore public storylines, use Storylines in the top menu.",

          by: "By",
          unclassifiedForum: "Unclassified Forum",
          created: "Created",
          scenes: "Scenes",

          max200Words: "max 200 words",
          edit: "Edit",
          add: "Add",
          save: "Save",
          cancel: "Cancel",
          saveAboutAuthor: "Save About Author",
          saveSynopsis: "Save Synopsis",
          saveImages: "Save Images",
          notProvidedYet: "Not provided yet.",
          images: "Images",
          coverSet: "Cover: set",
          coverNotSet: "Cover: not set",
          authorPhotoSet: "Author photo: set",
          authorPhotoNotSet: "Author photo: not set",
          useDirectImageLinks: "Use direct image links.",

          enterWritersRoom: "Enter Writer's Room",
          openReaderViewCard: "Open Reader View",

          loading: "Loading…",
          unknown: "Unknown",
          userLabel: "User",
          guestLabel: "Guest",
          writerRoleNeeded: "Writer",

          groupWriterRestrictedAlert:
          "You are not a member of this writing group. You may read the storyline, but you cannot enter the Writer's Room.",
          alertWriterFallback:
           "Could not open Writer Studio from here.",
          alertReaderFallback:
            "Could not open Reader View from here.",
           descriptionPlaceholderShort:
            "Update this storyline description…",
           aboutAuthorTitle: "About the Author",
        };
  }, [lang]);

  useEffect(() => {
    const focus =
      safeLocalGet("ns.storyHub.focusStoryId") || safeLocalGet("ns.selectedStoryId");
    if (focus) setFocusStoryId(String(focus));
    loadStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!Array.isArray(stories) || stories.length === 0) return;

    const ids = new Set(stories.map((s) => String(getId(s) || "")).filter(Boolean));

    const storedSelected = safeLocalGet("ns.selectedStoryId");
    const storedFocus = safeLocalGet("ns.storyHub.focusStoryId");

    const preferred =
      (storedSelected && ids.has(String(storedSelected)) && String(storedSelected)) ||
      (storedFocus && ids.has(String(storedFocus)) && String(storedFocus)) ||
      null;

    if (preferred && String(focusStoryId || "") !== preferred) {
      setFocusStoryId(preferred);
      safeLocalSet("ns.storyHub.focusStoryId", preferred);
      return;
    }

    const current = String(focusStoryId || "");
    if (current && ids.has(current)) return;

    const firstId = getId(stories[0]);
    if (firstId) {
      const fid = String(firstId);
      setFocusStoryId(fid);
      safeLocalSet("ns.storyHub.focusStoryId", fid);
      safeLocalSet("ns.selectedStoryId", fid);
      const firstForum = stories[0]?.forum ? String(stories[0].forum) : "";
      if (firstForum) safeLocalSet("ns.selectedForum", firstForum);
      try {
        window.dispatchEvent(new Event("ns:refresh"));
      } catch {}
    }
  }, [stories, focusStoryId]);

  useEffect(() => {
    if (!focusStoryId) return undefined;

    const frame = window.requestAnimationFrame(() => {
      const shelf = document.querySelector(".ns-storyhub-boardList");
      if (!shelf) return;

      shelf.scrollLeft = 0;
      shelf.scrollTop = 0;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [focusStoryId, storyHubTab]);

  function selectStory(story) {
    const id = getId(story);
    if (!id) return;

    const sid = String(id);

    if (String(focusStoryId || "") !== sid) {
      setStoryHubTab("overview");
      setDescriptionEditorOpenId(null);
      setDescriptionDraft("");
      setDescriptionSaveMsg("");
      closeSynopsisEditor();
      closeAboutEditor();
      closeMediaEditor();
      closePublicationEditor();
      closeVisibilityEditor();
    }

    setFocusStoryId(sid);

    safeLocalSet("ns.selectedStoryId", sid);
    safeLocalSet("ns.storyHub.focusStoryId", sid);
    safeLocalSet("ns.writer.focusStoryId", sid);
    safeLocalSet("ns.reader.focusStoryId", sid);

    if (story?.forum) safeLocalSet("ns.selectedForum", String(story.forum));

    try {
      window.dispatchEvent(new Event("ns:refresh"));
    } catch {}
  }

  function updateStoryInState(id, patch) {
    const sid = normalizeIdValue(id);
    if (!sid) return;

    setStories((prev) => {
      const next = (prev || []).map((s) => {
        const sId = getId(s);
        return String(sId || "") !== String(sid) ? s : { ...s, ...patch };
      });
      saveStoredStories(next);
      return next;
    });
  }

  async function ensureFullStoryLoaded(story) {
    const id = getId(story);
    if (!id) return story;

    const hasMeta =
      String(story?.description || "").trim() ||
      String(story?.synopsis || "").trim() ||
      String(story?.storyTeaser || "").trim() ||
      String(story?.aboutAuthor || "").trim() ||
      String(story?.authorPhotoUrl || "").trim() ||
      String(story?.coverImageUrl || "").trim() ||
      String(story?.publicationStatus || "").trim() ||
      String(story?.publicationUrl || "").trim() ||
      String(story?.publicationCoverUrl || "").trim() ||
      String(story?.publishedAuthorName || "").trim() ||
      String(story?.publishedAt || "").trim();

    if (hasMeta) return story;

    try {
      const full = await api(`/api/stories/${id}`);
      if (full && getId(full)) {
        updateStoryInState(id, {
          description: full.description || story.description || "",
          aboutAuthor: full.aboutAuthor || story.aboutAuthor || "",
          synopsis: full.synopsis || story.synopsis || "",
          storyTeaser: typeof full.storyTeaser === "string" ? full.storyTeaser : (story.storyTeaser || ""),
          authorPhotoUrl: full.authorPhotoUrl || story.authorPhotoUrl || "",
          coverImageUrl: full.coverImageUrl || story.coverImageUrl || "",
          publicationStatus: full.publicationStatus || story.publicationStatus || "inDevelopment",
          publicationUrl: full.publicationUrl || story.publicationUrl || "",
          publicationCoverUrl: full.publicationCoverUrl || story.publicationCoverUrl || "",
          publishedAuthorName: full.publishedAuthorName || story.publishedAuthorName || "",
          publishedAt: full.publishedAt || story.publishedAt || "",
          publicationNote: full.publicationNote || story.publicationNote || "",
          updatedAt: full.updatedAt || story.updatedAt || "",
        });
        return { ...story, ...full };
      }
    } catch {}
    return story;
  }

  async function loadStories() {
    const stored = loadStoredStories() || [];
    const storedSafe = Array.isArray(stored) ? stored : [];
    setStories(storedSafe);
    setLoadError("");

    try {
      setLoading(true);
      const remote = await api("/api/stories");

      if (Array.isArray(remote) && remote.length > 0) {
        const merged = mergeStories(remote, storedSafe);
        const preserved = preserveLocalFields(merged, storedSafe, [
          "synopsis",
          "aboutAuthor",
          "authorPhotoUrl",
          "coverImageUrl",
          "publicationStatus",
          "publicationTitle",
          "publicationSubtitle",
          "publicationUrl",
          "publicationCoverUrl",
          "publishedAuthorName",
          "publishedAt",
          "publicationNote",
        ]);

        setStories(preserved);
        saveStoredStories(preserved);
      }
    } catch (err) {
      console.error("StoryHub: error loading stories:", err?.message);
      if (storedSafe.length === 0) {
        setLoadError(
          lang === "fr"
            ? "Impossible de joindre le serveur. Aucune histoire chargée pour le moment."
            : "Could not reach the server. No storylines loaded yet."
        );
      }
    } finally {
      setLoading(false);
    }
  }


  function getDefaultCoverForNewStory(storyType, lang) {
    const isShort = String(storyType || "").trim().toLowerCase() === "short";
    const isFr = String(lang || "").trim().toLowerCase() === "fr";

    if (isFr) {
      return isShort
        ? "/images/default-covers/nouvelle-fr.webp"
        : "/images/default-covers/recit-fr.webp";
    }

    return isShort
      ? "/images/default-covers/short-story-en.webp"
      : "/images/default-covers/storyline-en.webp";
  }

  const writerAgreementText = lang === "fr"
    ? "En devenant Writer, vous confirmez que vous publierez uniquement du contenu original ou autorisé, que vous respecterez les autres utilisateurs, et que vous comprenez que NextScenes protège l’ordre de la Storyline par le Canon, les Propositions et les décisions de l’auteur. Vous acceptez que les abus, le contenu volé, haineux ou nuisible puissent entraîner des restrictions ou la suppression de l’accès Writer."
    : "By becoming a Writer, you confirm that you will publish only original or properly authorized work, respect other users, and understand that NextScenes protects each Storyline through Canon, Proposals, and author decisions. You agree that abuse, stolen work, hateful content, or harmful content may lead to restrictions or removal of Writer access.";

  async function handleBecomeWriter() {
    setWriterUpgradeMessage("");

    if (!isAuthenticated) {
      setWriterUpgradeMessage(lang === "fr" ? "Veuillez d’abord vous connecter." : "Please log in first.");
      if (onNavigatePage) onNavigatePage("login");
      return;
    }

    if (!writerAgreementChecked) {
      setWriterUpgradeMessage(
        lang === "fr"
          ? "Veuillez lire et accepter les conditions Writer avant de continuer."
          : "Please read and accept the Writer Agreement before continuing."
      );
      return;
    }

    if (!upgradeToWriter) {
      setWriterUpgradeMessage(
        lang === "fr"
          ? "La mise à niveau Writer n’est pas disponible pour le moment. Réessayez plus tard."
          : "Writer upgrade is not available right now. Please try again later."
      );
      return;
    }

    try {
      setWriterUpgradeBusy(true);
      const result = await upgradeToWriter({ accepted: true });
      if (refreshMe) await refreshMe();
      setWriterUpgradeMessage(
        lang === "fr"
          ? "Votre compte est maintenant Writer. Vous pouvez créer votre Storyline."
          : "Your account is now Writer. You can create your Storyline."
      );
      setCreateMessage("");
      setWriterAgreementOpen(false);
      return result;
    } catch (err) {
      const msg = String(err?.data?.message || err?.response?.data?.message || err?.message || "").trim();
      setWriterUpgradeMessage(
        msg ||
          (lang === "fr"
            ? "Impossible de mettre à niveau le compte pour le moment."
            : "Could not upgrade the account right now.")
      );
    } finally {
      setWriterUpgradeBusy(false);
    }
  }

  function validateCoverUploadFile(file) {
    if (!file) return { ok: false, message: t.coverUploadMissing };
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(String(file.type || "").toLowerCase())) {
      return { ok: false, message: t.coverUploadInvalid };
    }
    if (Number(file.size || 0) > 5 * 1024 * 1024) {
      return { ok: false, message: t.coverUploadInvalid };
    }
    return { ok: true, message: "" };
  }

  async function handleCoverUpload(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const check = validateCoverUploadFile(coverUploadFile);
    if (!check.ok) {
      setCoverUploadMessage(check.message);
      return;
    }

    try {
      setCoverUploading(true);
      setCoverUploadMessage("");
      const result = await api.uploadStoryCover(coverUploadFile);
      const url = String(result?.url || result?.publicUrl || "").trim();
      if (!url) throw new Error(t.coverUploadFailed);
      setCoverImageUrl(url);
      setCoverUploadMessage(t.coverUploadSuccess);
    } catch (err) {
      console.error("StoryHub cover upload failed:", err);
      setCoverUploadMessage(err?.message || t.coverUploadFailed);
    } finally {
      setCoverUploading(false);
    }
  }

  async function handleMediaCoverUpload(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();

    const check = validateCoverUploadFile(mediaCoverUploadFile);
    if (!check.ok) {
      setMediaCoverUploadMessage(check.message);
      return;
    }

    const activeStoryId = String(mediaEditorOpenId || "").trim();

    try {
      setMediaCoverUploading(true);
      setMediaCoverUploadMessage("");
      setMediaSaveMsg(lang === "fr" ? "Téléversement…" : "Uploading…");

      const result = await api.uploadStoryCover(mediaCoverUploadFile);
      const url = String(result?.url || result?.publicUrl || "").trim();
      if (!url) throw new Error(t.coverUploadFailed);

      setMediaCoverDraft(url);

      if (activeStoryId) {
        const patch = {
          authorPhotoUrl: String(mediaAuthorPhotoDraft || "").trim(),
          coverImageUrl: url,
        };

        const saveResult = await saveStoryMeta(activeStoryId, patch);
        if (saveResult.ok) {
          updateStoryInState(
            activeStoryId,
            saveResult.story && typeof saveResult.story === "object"
              ? saveResult.story
              : { ...patch, updatedAt: new Date().toISOString() }
          );
          setMediaCoverUploadMessage(
            lang === "fr"
              ? "Couverture téléversée et enregistrée."
              : "Cover uploaded and saved."
          );
          setMediaSaveMsg(lang === "fr" ? "Enregistré ✓" : "Saved ✓");
        } else {
          setMediaCoverUploadMessage(
            storySaveFailureMessage(
              saveResult,
              "Cover uploaded, but the storyline was not updated.",
              "Couverture téléversée, mais la storyline n’a pas été mise à jour."
            )
          );
          setMediaSaveMsg(
            lang === "fr"
              ? "Téléversé. Enregistrement à réessayer."
              : "Uploaded. Saving needs retry."
          );
        }
      } else {
        setMediaCoverUploadMessage(t.coverUploadSuccess);
        setMediaSaveMsg(
          lang === "fr"
            ? "URL ajoutée. Cliquez sur Enregistrer les images."
            : "URL added. Click Save Images."
        );
      }
    } catch (err) {
      console.error("StoryHub media cover upload failed:", err);
      setMediaCoverUploadMessage(err?.message || t.coverUploadFailed);
      setMediaSaveMsg("");
    } finally {
      setMediaCoverUploading(false);
    }
  }

  function validateAuthorPhotoUploadFile(file) {
    if (!file) return { ok: false, message: t.authorPhotoUploadMissing };
    const allowed = ["image/jpeg", "image/png", "image/webp"];
    if (!allowed.includes(String(file.type || "").toLowerCase())) {
      return { ok: false, message: t.authorPhotoUploadInvalid };
    }
    if (Number(file.size || 0) > 5 * 1024 * 1024) {
      return { ok: false, message: t.authorPhotoUploadInvalid };
    }
    return { ok: true, message: "" };
  }

  async function handleAuthorPhotoUpload(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    const check = validateAuthorPhotoUploadFile(authorPhotoUploadFile);
    if (!check.ok) {
      setAuthorPhotoUploadMessage(check.message);
      return;
    }

    try {
      setAuthorPhotoUploading(true);
      setAuthorPhotoUploadMessage("");
      const result = await api.uploadAuthorPhoto(authorPhotoUploadFile);
      const url = String(result?.url || result?.publicUrl || "").trim();
      if (!url) throw new Error(t.authorPhotoUploadFailed);
      setAuthorPhotoUrl(url);
      setAuthorPhotoUploadMessage(t.authorPhotoUploadSuccess);
    } catch (err) {
      console.error("StoryHub author photo upload failed:", err);
      setAuthorPhotoUploadMessage(err?.message || t.authorPhotoUploadFailed);
    } finally {
      setAuthorPhotoUploading(false);
    }
  }

  async function handleCreateStoryline(e) {
    e.preventDefault();
    setCreateMessage("");

    if (!isAuthenticated) {
      setCreateMessage(lang === "fr" ? "Veuillez d’abord vous connecter." : "Please log in first.");
      if (onNavigatePage) onNavigatePage("login");
      return;
    }

    if (!isWriterOrAdmin) {
      setWriterAgreementOpen(true);
      setCreateMessage(
        lang === "fr"
          ? "Pour créer une Storyline, acceptez d’abord les conditions Writer."
          : "To create a Storyline, please accept the Writer Agreement first."
      );
      return;
    }

    if (!title.trim()) {
      return setCreateMessage(
        lang === "fr"
          ? "Veuillez donner un titre à l’histoire."
          : "Please provide a title for the storyline."
      );
    }
    if (!forum.trim()) {
      return setCreateMessage(
        lang === "fr"
          ? "Veuillez choisir un forum (genre)."
          : "Please choose a forum (genre)."
      );
    }
    if (!description.trim()) {
      return setCreateMessage(
        lang === "fr"
          ? "Veuillez fournir une brève description."
          : "Please provide a brief description."
      );
    }

    const synWords = countWords(synopsis);
    const aboutWords = countWords(aboutAuthor);

    if (synWords > 150) {
      return setCreateMessage(
        lang === "fr"
          ? "Le synopsis est trop long. Veuillez le limiter à 150 mots."
          : "Synopsis is too long. Please keep it within 150 words."
      );
    }
    if (aboutWords > 150) {
      return setCreateMessage(
        lang === "fr"
          ? "Le texte “À propos de l’auteur” est trop long. Veuillez le limiter à 150 mots."
          : "About Author is too long. Please keep it within 150 words."
      );
    }

    const authorFromUser = getAuthorInfoFromUser(user, username);

    if (!authorFromUser.username || !authorFromUser.username.trim()) {
      return setCreateMessage(
        lang === "fr"
          ? "Veuillez définir un nom d’utilisateur avant de créer une histoire."
          : "Please set a username before creating a storyline."
      );
    }

    const isGroupMode = isGroupModeLabel(writingMode);
    let inviteEmails = [];
    if (isGroupMode) {
      const raw = String(inviteEmailsText || "").trim();
      if (!raw) {
        setInviteEmailsError(
          lang === "fr"
            ? "Les emails des invités sont obligatoires pour l’écriture de groupe."
            : "Invitee emails are required for Group Writing."
        );
        return setCreateMessage(
          lang === "fr"
            ? "L’écriture de groupe exige des emails d’invités."
            : "Group Writing requires invitee emails."
        );
      }

      inviteEmails = parseInviteEmails(inviteEmailsText);
      if (!Array.isArray(inviteEmails) || inviteEmails.length === 0) {
        setInviteEmailsError(
          lang === "fr"
            ? "Veuillez saisir des emails valides séparés par des virgules."
            : "Please enter valid emails separated by commas."
        );
        return setCreateMessage(
          lang === "fr"
            ? "Les emails des invités pour l’écriture de groupe sont invalides."
            : "Group Writing invitee emails are invalid."
        );
      }

      setInviteEmailsError("");
    } else {
      setInviteEmailsError("");
    }

    const nowIso = new Date().toISOString();

    const payload = {
      title: title.trim(),
      forum: forum.trim(),
      description: description.trim(),
      synopsis: synopsis.trim(),
      aboutAuthor: aboutAuthor.trim(),
      authorPhotoUrl: authorPhotoUrl.trim(),
      coverImageUrl: coverImageUrl.trim() || getDefaultCoverForNewStory(storyType, lang),
      storyType: storyType,
      mode: writingMode,
      writingMode: writingMode,
      visibilityStatus: normalizeStoryVisibilityStatus(visibilityStatus),
      visibility: legacyVisibilityFromStatus(visibilityStatus),

      createdAt: nowIso,
      updatedAt: nowIso,

      createdBy: authorFromUser.username.trim(),
      author: authorFromUser.username.trim(),

      createdById: authorFromUser.id || "",
      authorId: authorFromUser.id || "",
      authorUsername: authorFromUser.username.trim(),
      authorName: authorFromUser.name || "",
      authorAvatarUrl: authorFromUser.avatarUrl || "",

      authorProfile: {
        id: authorFromUser.id || "",
        username: authorFromUser.username.trim(),
        name: authorFromUser.name || "",
        avatarUrl: authorFromUser.avatarUrl || "",
      },
    };

    if (isGroupMode) payload.inviteEmails = inviteEmails;

    try {
      setCreating(true);

      let created = null;
      try {
        created = await api("/api/stories", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } catch (err) {
        const msg = String(err?.message || "").toLowerCase();
        if (msg.includes("forbidden") || msg.includes("403")) {
          setCreateMessage(
            lang === "fr"
              ? "Accès refusé : seuls les Writers peuvent créer des histoires."
              : "Forbidden: only Writers can create storylines."
          );
          return;
        }
        if (msg.includes("unauthorized") || msg.includes("401")) {
          setCreateMessage(
            lang === "fr" ? "Veuillez d’abord vous connecter." : "Please log in first."
          );
          return;
        }
        throw err;
      }

      const createdIdRaw = (created && (created._id || created.id)) || null;
      const createdId = normalizeIdValue(createdIdRaw);
      const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const finalId = createdId || localId;

      const withScenes = {
        ...(created || {}),
        ...payload,
        _id: finalId,
        id: finalId,
        scenes: (created && created.scenes) || [],
        createdAt: (created && created.createdAt) || nowIso,
        updatedAt: (created && created.updatedAt) || nowIso,
        mode: (created && (created.mode || created.writingMode)) || writingMode,
        writingMode: (created && (created.writingMode || created.mode)) || writingMode,
        synopsis: (created && created.synopsis) || payload.synopsis || "",
        aboutAuthor: (created && created.aboutAuthor) || payload.aboutAuthor || "",
        authorPhotoUrl: (created && created.authorPhotoUrl) || payload.authorPhotoUrl || "",
        coverImageUrl: (created && created.coverImageUrl) || payload.coverImageUrl || "",
        visibilityStatus: (created && created.visibilityStatus) || payload.visibilityStatus || "public_preview",
        visibility: (created && created.visibility) || payload.visibility || "public",
      };

      setStories((prev) => {
        const next = [
          withScenes,
          ...prev.filter((s) => String(getId(s) || "") !== String(finalId)),
        ];
        saveStoredStories(next);
        return next;
      });

      safeLocalSet("ns.selectedStoryId", String(finalId));
      safeLocalSet("ns.storyHub.focusStoryId", String(finalId));
      if (withScenes?.forum) safeLocalSet("ns.selectedForum", String(withScenes.forum));
      setFocusStoryId(String(finalId));

      setTitle("");
      setForum("");
      setDescription("");
      setSynopsis("");
      setAboutAuthor("");
      setAuthorPhotoUrl("");
      setCoverImageUrl("");
      setCoverUploadFile(null);
      setCoverUploadMessage("");
      setWritingMode("Solo");
      setStoryType("longform");
      setVisibilityStatus("public_preview");

      setInviteEmailsText("");
      setInviteEmailsError("");

      setCreateMessage(
        lang === "fr"
          ? `Histoire créée avec succès et attribuée à ${authorFromUser.display}.`
          : `Storyline created successfully and attributed to ${authorFromUser.display}.`
      );

      try {
        window.dispatchEvent(new Event("ns:refresh"));
      } catch {}

      if (
        createdId &&
        typeof onNavigatePage === "function"
      ) {
        onNavigatePage(
          "projectHub",
          {
            projectCategory: "story",
            projectId: String(createdId),
            projectTitle:
              String(
                withScenes?.title ||
                payload?.title ||
                ""
              ).trim(),
          }
        );
      }
    } catch (err) {
      const msg = String(err?.message || "");
      console.error("StoryHub: create storyline error:", msg);
      setCreateMessage(
        lang === "fr"
          ? "Impossible de créer l’histoire. Vérifiez votre connexion et réessayez."
          : "Could not create storyline. Please check your connection and try again."
      );
    } finally {
      setCreating(false);
    }
  }

  const myStories = useMemo(() => {
    if (!isAuthenticated) return [];

    const myId = String(user?.id || user?._id || "").trim();
    const myUsername = String(user?.username || username || "").trim().toLowerCase();

    if (!myId && !myUsername) return [];

    const mine = stories.filter((s) => {
      const author = getAuthorInfoFromStory(s);

      const storyAuthorId = String(author?.authorId || s?.authorId || s?.createdById || "").trim();

      const storyAuthorUsername = String(
        author?.authorUsername || s?.authorUsername || s?.createdBy || s?.author || ""
      )
        .trim()
        .toLowerCase();

      if (myId && storyAuthorId && myId === storyAuthorId) return true;
      if (myUsername && storyAuthorUsername && myUsername === storyAuthorUsername) return true;

      return false;
    });

    const copy = [...mine].sort((a, b) => {
      const ta = new Date(getStoryCreatedAt(a) || 0).getTime() || 0;
      const tb = new Date(getStoryCreatedAt(b) || 0).getTime() || 0;
      return tb - ta;
    });

    if (focusStoryId) {
      const fid = String(focusStoryId);
      copy.sort((a, b) => {
        const aId = String(getId(a) || "");
        const bId = String(getId(b) || "");
        if (aId === fid) return -1;
        if (bId === fid) return 1;
        return 0;
      });
    }

    return copy;
  }, [stories, username, user, isAuthenticated, focusStoryId]);

  function handleOpenWriterRoom(story) {
    const id = getId(story);
    if (!id) return;

    if (isGroupStory(story)) {
      const isAdminRole = String(role || "").trim().toLowerCase() === "admin";
      const member = isInvitedGroupMember(story, user, username);

      if (!isAdminRole && !member) {
        const mode = getGroupMode(story);
        const label =
          mode === "closed"
            ? lang === "fr"
              ? "Groupe (fermé)"
              : "Group (Closed)"
            : lang === "fr"
            ? "Groupe (ouvert)"
            : "Group (Open)";
        alert(`${label}: ${t.groupWriterRestrictedAlert}`);
return handleOpenReader(story);
      }
    }

    selectStory(story);
    safeLocalSet("ns.openWriterStudio", "1");

    if (onEnterWriterRoom) return onEnterWriterRoom(story);
    if (onNavigatePage) return onNavigatePage("writerStudio");
    alert(t.alertWriterFallback);
  }

  function handleOpenReader(story) {
    const id = getId(story);
    if (!id) return;

    selectStory(story);

    if (onOpenReaderView) return onOpenReaderView(story);
    if (onNavigatePage) return onNavigatePage("readerView");
    alert(t.alertReaderFallback);
  }

  function handleOpenPublicationLink(story) {
    const info = getPublicationInfo(story, lang);
    const href = String(info?.publicationUrl || info?.amazonHref || "").trim();
    if (!href) return handleOpenReader(story);

    try {
      window.open(href, "_blank", "noopener,noreferrer");
    } catch {
      window.location.href = href;
    }
  }

  function openDescriptionEditor(storyId, currentText) {
    setDescriptionEditorOpenId(storyId);
    setDescriptionDraft(String(currentText || ""));
    setDescriptionSaveMsg("");

    if (synopsisEditorOpenId && synopsisEditorOpenId !== storyId) {
      setSynopsisEditorOpenId(null);
    }
    if (aboutEditorOpenId && aboutEditorOpenId !== storyId) {
      setAboutEditorOpenId(null);
    }
  }

  function openSynopsisEditor(story) {
    const id = String(getId(story) || "");
    if (!id) return;
    setSynopsisSaveMsg("");
    setSynopsisEditorOpenId(id);
    setSynopsisDraft(String(story.synopsis || "").trim());

    if (descriptionEditorOpenId) setDescriptionEditorOpenId(null);
    if (aboutEditorOpenId) setAboutEditorOpenId(null);
  }

  function closeSynopsisEditor() {
    setSynopsisEditorOpenId(null);
    setSynopsisDraft("");
    setSynopsisSaveMsg("");
  }

  async function openAboutEditor(story) {
    const id = String(getId(story) || "");
    if (!id) return;
    setAboutSaveMsg("");
    setAboutEditorOpenId(id);

    if (descriptionEditorOpenId) setDescriptionEditorOpenId(null);
    if (synopsisEditorOpenId) setSynopsisEditorOpenId(null);

    const fullish = await ensureFullStoryLoaded(story);
    setAboutDraft(String(fullish?.aboutAuthor || "").trim());
  }

  function closeAboutEditor() {
    setAboutEditorOpenId(null);
    setAboutDraft("");
    setAboutSaveMsg("");
  }

  async function openMediaEditor(story) {
    const id = String(getId(story) || "");
    if (!id) return;
    setMediaSaveMsg("");
    setMediaCoverUploadFile(null);
    setMediaCoverUploadMessage("");
    setMediaEditorOpenId(id);

    const fullish = await ensureFullStoryLoaded(story);
    setMediaAuthorPhotoDraft(String(fullish?.authorPhotoUrl || "").trim());
    setMediaCoverDraft(String(fullish?.coverImageUrl || "").trim());
  }

  function closeMediaEditor() {
    setMediaEditorOpenId(null);
    setMediaAuthorPhotoDraft("");
    setMediaCoverDraft("");
    setMediaCoverUploadFile(null);
    setMediaCoverUploadMessage("");
    setMediaSaveMsg("");
  }

  async function saveStoryMeta(id, patch) {
    try {
      const savedStory = await api.put(
        `/api/stories/${encodeURIComponent(String(id))}`,
        patch
      );
      return { ok: true, story: savedStory || null, message: "" };
    } catch (err) {
      const message =
        String(err?.message || "").trim() ||
        (lang === "fr"
          ? "Le serveur n’a pas confirmé l’enregistrement."
          : "The server did not confirm the save.");
      console.error("StoryHub storyline save failed:", err);
      return { ok: false, story: null, message };
    }
  }

  function storySaveFailureMessage(result, fallbackEn, fallbackFr) {
    const fallback = lang === "fr" ? fallbackFr : fallbackEn;
    const detail = String(result?.message || "").trim();
    return detail ? `${fallback} ${detail}` : fallback;
  }

  async function saveDescriptionForStory(story) {
    const storyId = getId(story);
    if (!storyId) return;

    const nextText = String(descriptionDraft || "").trim();
    const words = countWords(nextText);
    if (words > 200) {
      setDescriptionSaveMsg(
        lang === "fr"
          ? `Trop long : ${words}/200 mots. Veuillez raccourcir.`
          : `Too long: ${words}/200 words. Please shorten.`
      );
      return;
    }

    setDescriptionSaveMsg(lang === "fr" ? "Enregistrement..." : "Saving...");
    const result = await saveStoryMeta(storyId, { description: nextText });

    if (result.ok) {
      updateStoryInState(
        storyId,
        result.story && typeof result.story === "object"
          ? result.story
          : { description: nextText, updatedAt: new Date().toISOString() }
      );
      setDescriptionSaveMsg(lang === "fr" ? "Enregistré ✓" : "Saved ✓");
      setTimeout(() => setDescriptionSaveMsg(""), 1200);
      setDescriptionEditorOpenId(null);
    } else {
      setDescriptionSaveMsg(
        storySaveFailureMessage(
          result,
          "Could not save.",
          "Impossible d’enregistrer."
        )
      );
    }
  }

  async function saveSynopsisForStory(story) {
    const id = getId(story);
    if (!id) return;

    const draft = String(synopsisDraft || "").trim();
    if (countWords(draft) > 150) {
      setSynopsisSaveMsg(
        lang === "fr"
          ? "Trop long. Limitez-vous à 150 mots."
          : "Too long. Keep within 150 words."
      );
      return;
    }

    const newSynopsis = clampWords(draft, 150);
    setSynopsisSaveMsg(lang === "fr" ? "Enregistrement..." : "Saving...");

    const result = await saveStoryMeta(id, { synopsis: newSynopsis });
    if (!result.ok) {
      setSynopsisSaveMsg(
        storySaveFailureMessage(
          result,
          "Could not save.",
          "Impossible d’enregistrer."
        )
      );
      return;
    }

    updateStoryInState(
      id,
      result.story && typeof result.story === "object"
        ? result.story
        : { synopsis: newSynopsis, updatedAt: new Date().toISOString() }
    );
    setSynopsisSaveMsg(lang === "fr" ? "Enregistré ✓" : "Saved ✓");
    setSynopsisEditorOpenId(null);
    setSynopsisDraft("");
  }

  async function saveAboutForStory(story) {
    const id = getId(story);
    if (!id) return;

    const draft = String(aboutDraft || "").trim();
    if (countWords(draft) > 150) {
      setAboutSaveMsg(
        lang === "fr"
          ? "Trop long. Limitez-vous à 150 mots."
          : "Too long. Keep within 150 words."
      );
      return;
    }

    const newAbout = clampWords(draft, 150);
    setAboutSaveMsg(lang === "fr" ? "Enregistrement..." : "Saving...");

    const result = await saveStoryMeta(id, { aboutAuthor: newAbout });
    if (!result.ok) {
      setAboutSaveMsg(
        storySaveFailureMessage(
          result,
          "Could not save.",
          "Impossible d’enregistrer."
        )
      );
      return;
    }

    updateStoryInState(
      id,
      result.story && typeof result.story === "object"
        ? result.story
        : { aboutAuthor: newAbout, updatedAt: new Date().toISOString() }
    );
    setAboutSaveMsg(lang === "fr" ? "Enregistré ✓" : "Saved ✓");
    setAboutEditorOpenId(null);
    setAboutDraft("");
  }


  function openVisibilityEditor(story) {
    const id = getId(story);
    if (!id) return;
    setVisibilityEditorOpenId(String(id));
    setVisibilityDraft(getStoryVisibilityStatus(story));
    setVisibilitySaveMsg("");
  }

  function closeVisibilityEditor() {
    setVisibilityEditorOpenId(null);
    setVisibilityDraft("public_preview");
    setVisibilitySaveMsg("");
  }

  async function saveVisibilityForStory(story) {
    const id = getId(story);
    if (!id) return;

    const nextStatus = normalizeStoryVisibilityStatus(visibilityDraft);
    const patch = {
      visibilityStatus: nextStatus,
      visibility: legacyVisibilityFromStatus(nextStatus),
    };

    setVisibilitySaveMsg(lang === "fr" ? "Enregistrement..." : "Saving...");
    const result = await saveStoryMeta(id, patch);

    if (result.ok) {
      updateStoryInState(
        id,
        result.story && typeof result.story === "object"
          ? result.story
          : { ...patch, updatedAt: new Date().toISOString() }
      );
      setVisibilitySaveMsg(lang === "fr" ? "Visibilité enregistrée ✓" : "Visibility saved ✓");
      setTimeout(() => setVisibilitySaveMsg(""), 1200);
      closeVisibilityEditor();
    } else {
      setVisibilitySaveMsg(
        storySaveFailureMessage(
          result,
          "Could not save visibility.",
          "Impossible d’enregistrer la visibilité."
        )
      );
    }
  }


  function openPublicationEditor(story) {
    const id = getId(story);
    if (!id) return;
    setPublicationEditorOpenId(String(id));
    setPublicationSaveMsg("");
    setPublicationDraft({
      publicationStatus: String(story?.publicationStatus || "inDevelopment").trim() || "inDevelopment",
      publicationTitle: String(story?.publicationTitle || story?.publishedTitle || story?.title || "").trim(),
      publicationSubtitle: String(story?.publicationSubtitle || story?.publishedSubtitle || "").trim(),
      publishedAuthorName: String(story?.publishedAuthorName || story?.authorName || story?.authorProfile?.name || "").trim(),
      publicationUrl: String(story?.publicationUrl || story?.amazonUrl || story?.amazonPageUrl || "").trim(),
      publicationCoverUrl: String(story?.publicationCoverUrl || story?.coverImageUrl || "").trim(),
      publishedAt: String(story?.publishedAt || "").trim(),
      publicationNote: String(story?.publicationNote || "").trim(),
    });
  }

  function closePublicationEditor() {
    setPublicationEditorOpenId(null);
    setPublicationSaveMsg("");
  }

  async function savePublicationForStory(story) {
    const id = getId(story);
    if (!id) return;

    const patch = {
      publicationStatus: String(publicationDraft.publicationStatus || "inDevelopment").trim(),
      publicationTitle: String(publicationDraft.publicationTitle || "").trim(),
      publicationSubtitle: String(publicationDraft.publicationSubtitle || "").trim(),
      publishedAuthorName: String(publicationDraft.publishedAuthorName || "").trim(),
      publicationUrl: String(publicationDraft.publicationUrl || "").trim(),
      publicationCoverUrl: String(publicationDraft.publicationCoverUrl || "").trim(),
      publishedAt: String(publicationDraft.publishedAt || "").trim(),
      publicationNote: String(publicationDraft.publicationNote || "").trim(),
      updatedAt: new Date().toISOString(),
    };

    setPublicationSaveMsg(lang === "fr" ? "Enregistrement..." : "Saving...");

    const result = await saveStoryMeta(id, patch);
    if (result.ok) {
      updateStoryInState(
        id,
        result.story && typeof result.story === "object" ? result.story : patch
      );
      setPublicationSaveMsg(lang === "fr" ? "Paramètres de publication enregistrés ✓" : "Publication settings saved ✓");
      setTimeout(() => setPublicationSaveMsg(""), 1400);
      setPublicationEditorOpenId(null);
    } else {
      setPublicationSaveMsg(
        storySaveFailureMessage(
          result,
          "Could not save publication settings.",
          "Impossible d’enregistrer les paramètres de publication."
        )
      );
    }
  }

  async function saveMediaForStory(story) {
    const id = getId(story);
    if (!id) return;

    const patch = {
      authorPhotoUrl: String(mediaAuthorPhotoDraft || "").trim(),
      coverImageUrl: String(mediaCoverDraft || "").trim(),
    };

    setMediaSaveMsg(lang === "fr" ? "Enregistrement…" : "Saving…");

    const result = await saveStoryMeta(id, patch);

    if (result.ok) {
      updateStoryInState(
        id,
        result.story && typeof result.story === "object"
          ? result.story
          : { ...patch, updatedAt: new Date().toISOString() }
      );
      setMediaSaveMsg(lang === "fr" ? "Enregistré ✓" : "Saved ✓");
      setMediaEditorOpenId(null);
      setMediaCoverUploadFile(null);
      setMediaCoverUploadMessage("");
      return;
    }

    setMediaSaveMsg(
      storySaveFailureMessage(
        result,
        "Could not save.",
        "Impossible d’enregistrer."
      )
    );
  }

  const storyHubBackgroundUrl = `${process.env.PUBLIC_URL || ""}/images/storyhub/storyhub-background.png`;

  const storyHubTabs = [
    { key: "overview", label: lang === "fr" ? "Vue d’ensemble" : "Overview" },
    { key: "details", label: lang === "fr" ? "Détails" : "Story Details" },
    { key: "author", label: lang === "fr" ? "Auteur & synopsis" : "Author & Synopsis" },
    { key: "images", label: lang === "fr" ? "Images" : "Images" },
    { key: "publication", label: lang === "fr" ? "Visibilité et publication" : "Visibility & Publication" },
    { key: "actions", label: lang === "fr" ? "Actions" : "Actions" },
  ];

  return (
    <div
      className="ns-page story-hub ns-library-hall"
      style={{ "--ns-storyhub-bg": `url("${storyHubBackgroundUrl}")` }}
    >
      <ProjectBreadcrumbTrail
        onNavigatePage={onNavigatePage}
        lang={lang}
        pageKey="createNovelStory"
        projectTitle={
          projectHubContext?.projectTitle
        }
      />

      <header
        className="ns-page-header ns-identity-hero storyhub-identity-hero"
        style={{
          backgroundImage:
            `linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url("${storyHubBackgroundUrl}")`,
        }}
      >
        <div className="ns-identity-hero-copy">
          <div className="ns-identity-kicker">NextScenes</div>
          <h1 className="ns-page-title ns-identity-title">{t.pageTitle}</h1>
          <p className="ns-page-subtitle ns-identity-subtitle">
            {isWriterOrAdmin ? t.pageSubtitleWriter : t.pageSubtitleUser}
          </p>



          {loadError && (
            <p className="ns-card-meta" style={{ marginTop: 12, color: "#fecaca" }}>
              {loadError}
            </p>
          )}
        </div>

        <div className="ns-identity-hero-stat" aria-live="polite">
          <span className="ns-identity-stat-number">
            {isWriterOrAdmin ? myStories.length : stories.length}
          </span>
          <span className="ns-identity-stat-label">
            {isWriterOrAdmin ? t.yourStorylines : t.writerBoards}
          </span>
        </div>
      </header>

      <div className="story-hub-grid">
        <section className="ns-column">
          <div className="ns-card ns-card-soft">
            <h2 className="ns-section-title">
              {lang === "fr" ? "Créer un Roman / Histoire" : "Create a Novel / Story"}
            </h2>

            <div
              className="ns-storyhub-formImagePanel"
              aria-hidden="true"
              style={{
                backgroundImage:
                  `linear-gradient(90deg, rgba(6, 78, 59, 0.72), rgba(6, 78, 59, 0.20)), url("${storyHubBackgroundUrl}")`,
              }}
            >
              <span>{lang === "fr" ? "Commencez par l’idée claire." : "Begin with the clear idea."}</span>
            </div>

            {!isAuthenticated ? (
              <div className="ns-card-meta" style={{ marginTop: 8 }}>
                <p style={{ marginTop: 0 }}>
                  {lang === "fr" ? (
                    <>
                      Vous naviguez actuellement en tant qu’<strong>{t.guestLabel}</strong>. L’inscription est gratuite : connectez-vous pour créer des histoires.
                    </>
                  ) : (
                    <>
                      You are browsing as a <strong>{t.guestLabel}</strong>. Registration is free: log in to create storylines.
                    </>
                  )}
                </p>
                <div className="ns-trust-notice ns-trust-notice-compact" role="note" style={{ marginTop: 10 }}>
                  <div className="ns-trust-notice-icon" aria-hidden="true">✓</div>
                  <div>
                    <div className="ns-trust-notice-title">
                      {lang === "fr" ? "Accès gratuit" : "Free core access"}
                    </div>
                    <p className="ns-trust-notice-body">
                      {lang === "fr"
                        ? "Lire, s’inscrire et participer aux activités de base sur NextScenes ne comporte aucun frais caché."
                        : "Reading, registration, and core NextScenes participation have no hidden fees."}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="ns-button ns-button-primary"
                  onClick={() => (onNavigatePage ? onNavigatePage("login") : alert(lang === "fr" ? "Veuillez vous connecter." : "Please log in."))}
                  style={{ marginTop: 8 }}
                >
                  {t.login}
                </button>
              </div>
            ) : !isWriterOrAdmin ? (
              <div className="ns-card-meta" style={{ marginTop: 8 }}>
                <p style={{ marginTop: 0 }}>
                  {lang === "fr" ? (
                    <>
                      Vous êtes actuellement <strong>{t.userLabel}</strong>
                      {role ? <span> ({role})</span> : null}.
                    </>
                  ) : (
                    <>
                      You are currently a <strong>{t.userLabel}</strong>
                      {role ? <span> ({role})</span> : null}.
                    </>
                  )}
                </p>
                <p style={{ marginTop: 8 }}>{t.currentUserRole2}</p>
                <p style={{ marginTop: 8 }}>
                  {lang === "fr"
                    ? "Pour créer votre première Storyline, devenez Writer en acceptant les conditions ci-dessous. Cette participation de base reste gratuite."
                    : "To create your first Storyline, become a Writer by accepting the agreement below. Core participation remains free."}
                </p>

                <button
                  type="button"
                  className="ns-button ns-button-primary"
                  onClick={() => setWriterAgreementOpen((v) => !v)}
                  style={{ marginTop: 10 }}
                >
                  {writerAgreementOpen
                    ? (lang === "fr" ? "Masquer les conditions Writer" : "Hide Writer Agreement")
                    : (lang === "fr" ? "Devenir Writer" : "Become a Writer")}
                </button>

                {writerAgreementOpen ? (
                  <div className="ns-trust-notice ns-trust-notice-compact" role="note" style={{ marginTop: 12 }}>
                    <div className="ns-trust-notice-icon" aria-hidden="true">✓</div>
                    <div>
                      <div className="ns-trust-notice-title">
                        {lang === "fr" ? "Conditions Writer" : "Writer Agreement"}
                      </div>
                      <p className="ns-trust-notice-body">{writerAgreementText}</p>

                      <label className="ns-register-checkbox-row" style={{ marginTop: 10 }}>
                        <input
                          type="checkbox"
                          checked={writerAgreementChecked}
                          onChange={(e) => setWriterAgreementChecked(!!e.target.checked)}
                          disabled={writerUpgradeBusy}
                        />
                        <span>
                          {lang === "fr"
                            ? "J’ai lu et j’accepte les conditions Writer."
                            : "I have read and accept the Writer Agreement."}
                        </span>
                      </label>

                      <button
                        type="button"
                        className="ns-button ns-button-primary"
                        disabled={writerUpgradeBusy || !writerAgreementChecked}
                        onClick={handleBecomeWriter}
                        style={{ marginTop: 10 }}
                      >
                        {writerUpgradeBusy
                          ? (lang === "fr" ? "Mise à niveau…" : "Upgrading…")
                          : (lang === "fr" ? "Accepter et devenir Writer" : "Accept and become Writer")}
                      </button>
                    </div>
                  </div>
                ) : null}

                {writerUpgradeMessage ? (
                  <div className="banner" style={{ marginTop: 10 }}>
                    {writerUpgradeMessage}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="ns-trust-notice ns-trust-notice-compact" role="note" data-storyhub-creation-consolidated="true" style={{ marginTop: 12 }}>
                <div className="ns-trust-notice-icon" aria-hidden="true">✓</div>
                <div>
                  <div className="ns-trust-notice-title">
                    {lang === "fr"
                      ? "Une seule entrée pour créer des histoires"
                      : "One place to create new stories"}
                  </div>
                  <p className="ns-trust-notice-body">
                    {lang === "fr"
                      ? "Les nouveaux projets Roman / Histoire commencent dans le Hub de projets et s’ouvrent dans l’Espace Livre. Vos histoires existantes et leurs outils de gestion restent disponibles ici."
                      : "Start new Novel / Story projects in Project Hub and continue in Book Workspace. Your existing stories and their management tools remain available here."}
                  </p>
                  <button
                    type="button"
                    className="ns-button ns-button-primary"
                    data-storyhub-create-in-project-hub="true"
                    onClick={() =>
                      onNavigatePage?.("projectHub", { projectCategory: "story" })
                    }
                    style={{ marginTop: 10 }}
                  >
                    {lang === "fr" ? "Créer dans le Hub de projets" : "Create in Project Hub"}
                  </button>
                </div>
              </div>
            )}

            {!isWriterOrAdmin && createMessage ? (
              <p className="ns-card-meta" style={{ marginTop: 10 }}>
                {createMessage}
              </p>
            ) : null}
          </div>
        </section>

        <section className="ns-column">
          <div className="ns-card">
            <h2 className="ns-section-title">
              {isWriterOrAdmin ? t.yourStorylines : t.writerBoards}
            </h2>

            {!isAuthenticated ? (
              <p className="ns-card-meta">{t.guestRight}</p>
            ) : !username || !username.trim() ? (
              <p className="ns-card-meta">{t.noUsername}</p>
            ) : (
              <p className="ns-card-meta">
                {t.loggedInAs} <strong>{username}</strong>
                {user?.role ? <span> ({user.role})</span> : null}.
              </p>
            )}

            {loading && <p className="ns-card-meta">{t.loading}</p>}

            {!loading &&
              isWriterOrAdmin &&
              isAuthenticated &&
              username &&
              username.trim() &&
              myStories.length === 0 && (
                <p className="ns-card-meta" style={{ marginTop: "6px" }}>
                  {t.noStorylinesYet}
                </p>
              )}

            {!loading && !isWriterOrAdmin && (
              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                {t.onlyWritersSeeOwned}
              </p>
            )}

            {!loading && myStories.length > 0 && (
              <div
                className={
                  "ns-storyhub-boardList" +
                  (
                    descriptionEditorOpenId ||
                    synopsisEditorOpenId ||
                    aboutEditorOpenId ||
                    mediaEditorOpenId ||
                    publicationEditorOpenId ||
                    visibilityEditorOpenId
                      ? " is-editing-story"
                      : ""
                  )
                }
              >
                {myStories.map((story) => {
                  const sid = String(getId(story) || "");
                  const focused = focusStoryId && String(focusStoryId) === sid;

                  const createdIso = getStoryCreatedAt(story);
                  const createdDate = createdIso
                    ? new Date(createdIso).toLocaleDateString(lang === "fr" ? "fr-FR" : undefined)
                    : t.unknown;
                  const scenesCount = Array.isArray(story.scenes) ? story.scenes.length : 0;

                  const modeDisplay =
                    lang === "fr"
                      ? WRITING_MODE_LABELS_FR[story.mode || story.writingMode || "Solo"] ||
                        story.mode ||
                        story.writingMode ||
                        "Solo"
                      : story.mode || story.writingMode || "Solo";

                  const publicationInfo = getPublicationInfo(story, lang);
                  const isPublishedBookCard = isPublishedBook(story) || publicationInfo.isPublished;
                  const displayTitle =
                    (isPublishedBookCard && String(publicationInfo.publishedTitle || "").trim()) ||
                    String(story?.title || "").trim() ||
                    (lang === "fr" ? "Storyline sans titre" : "Untitled Storyline");
                  const displaySubtitle = isPublishedBookCard
                    ? String(publicationInfo.publishedSubtitle || story?.publicationSubtitle || "").trim()
                    : String(story?.subtitle || "").trim();
                  const forumDisplay =
                    (lang === "fr" ? FORUM_LABELS_FR[story.forum] || story.forum : story.forum) ||
                    t.unclassifiedForum;
                  const publishedDate = String(publicationInfo.publishedAt || story.publishedAt || "").trim();
                  const publishedDateLabel = publishedDate
                    ? new Date(publishedDate).toLocaleDateString(lang === "fr" ? "fr-FR" : undefined)
                    : "";
                  const baseAuthor = getAuthorInfoFromStory(story);
                  const publishedAuthorName = safeStr(story?.publishedAuthorName).trim();
                  const author =
                    publicationInfo.isPublished && publishedAuthorName
                      ? { ...baseAuthor, display: publishedAuthorName }
                      : baseAuthor;
                  const accent = getForumAccentColor(story?.forum);

                  const descriptionOpen = descriptionEditorOpenId && descriptionEditorOpenId === sid;
                  const synopsisOpen = synopsisEditorOpenId && synopsisEditorOpenId === sid;
                  const aboutOpen = aboutEditorOpenId && aboutEditorOpenId === sid;
                  const mediaOpen = mediaEditorOpenId && mediaEditorOpenId === sid;
                  const publicationOpen = publicationEditorOpenId && publicationEditorOpenId === sid;
                  const visibilityOpen = visibilityEditorOpenId && visibilityEditorOpenId === sid;
                  const visibilityCopy = getStoryVisibilityCopy(getStoryVisibilityStatus(story), lang);

                  return (
                    <article
                      key={sid || `${safeStr(story?.title)}_${Math.random().toString(36).slice(2)}`}
                      className={
                        "ns-card ns-card-soft ns-storyhub-storyCard" +
                        (focused ? " ns-card-active" : "") +
                        (focused && (descriptionOpen || synopsisOpen || aboutOpen || mediaOpen || publicationOpen || visibilityOpen) ? " is-editing" : "") +
                        (focused && mediaOpen ? " is-media-editing" : "") +
                        (isPublishedBookCard ? " is-published-book" : "")
                      }
                      style={{
                        marginBottom: "10px",
                        border: focused ? `2px solid ${accent}` : "1px solid rgba(15,23,42,0.10)",
                        boxShadow: focused ? "0 14px 28px rgba(15, 23, 42, 0.14)" : undefined,
                        background: focused ? "rgba(246, 242, 234, 0.55)" : undefined,
                        cursor: "pointer",
                      }}
                      onClick={() => selectStory(story)}
                    >
                      <div className="ns-storyhub-cardHead" style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                        <StoryIdentityTile story={story} />

                        <div className="ns-storyhub-cardBody" style={{ flex: 1, minWidth: 0 }}>
                          <div
                            className="ns-storyhub-titleRow"
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              justifyContent: "space-between",
                              gap: 10,
                            }}
                          >
                            <h3 className="ns-card-title" style={{ margin: 0, minWidth: 0 }}>
                              {displayTitle}
                            </h3>
                            {isPublishedBookCard ? (
                              <span className="ns-publication-chip is-published">
                                {publicationInfo.publicationLabel}
                              </span>
                            ) : null}
                            <VisibilityStatusChip status={getStoryVisibilityStatus(story)} lang={lang} />

                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                              <UserAvatar name={author.display} photoUrl={author.bestPhoto} />
                            </div>
                          </div>

                          {displaySubtitle ? (
                            <p className="ns-card-meta" style={{ margin: "5px 0 0", fontStyle: "italic", color: "#475569" }}>
                              {displaySubtitle}
                            </p>
                          ) : null}

                          <div style={{ marginTop: 6 }}>
                            <p className="ns-card-meta" style={{ margin: 0 }}>
                              {t.by} {author.display}
                            </p>
                            <p className="ns-card-meta" style={{ margin: 0, marginTop: 2 }}>
                              {isPublishedBookCard ? (
                                <>
                                  {forumDisplay} · {publicationInfo.developmentLabel}
                                  {publishedDateLabel
                                    ? ` · ${lang === "fr" ? "Publié" : "Published"}: ${publishedDateLabel}`
                                    : ""}
                                </>
                              ) : (
                                <>
                                  {forumDisplay} · {modeDisplay} · {t.created}: {createdDate} · {t.scenes}: {scenesCount}
                                </>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {focused ? (
                        <div className="ns-storyhub-tabs" onClick={(e) => e.stopPropagation()}>
                          {storyHubTabs.map((tab) => (
                            <button
                              key={tab.key}
                              type="button"
                              className={
                                "ns-storyhub-tab" + (storyHubTab === tab.key ? " is-active" : "")
                              }
                              onClick={() => setStoryHubTab(tab.key)}
                            >
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      ) : null}

                      {focused && storyHubTab === "overview" ? (
                        <div className="ns-storyhub-tabPanel">
                          <div className="ns-storyhub-overviewGrid">
                            <div>
                              <div className="ns-storyhub-panelEyebrow">
                                {isPublishedBookCard
                                  ? lang === "fr"
                                    ? "Livre publié"
                                    : "Published book"
                                  : lang === "fr"
                                  ? "Résumé rapide"
                                  : "Quick view"}
                              </div>
                              <h4 className="ns-card-title" style={{ marginTop: 4 }}>
                                {t.description}
                              </h4>
                              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                                {String(story.description || "").trim()
                                  ? shortSnippet(story.description, 260)
                                  : t.notProvidedYet}
                              </p>
                            </div>

                            <div className="ns-storyhub-miniFacts">
                              {isPublishedBookCard ? (
                                <>
                                  <div>
                                    <span>{lang === "fr" ? "Statut" : "Status"}</span>
                                    <strong>{publicationInfo.publicationLabel}</strong>
                                  </div>
                                  <div>
                                    <span>{lang === "fr" ? "Lecture" : "Reading"}</span>
                                    <strong>{publicationInfo.developmentLabel}</strong>
                                  </div>
                                  <div>
                                    <span>{lang === "fr" ? "Disponibilité" : "Availability"}</span>
                                    <strong>
                                      {publicationInfo.amazonHref
                                        ? lang === "fr"
                                          ? "Amazon"
                                          : "Amazon"
                                        : lang === "fr"
                                        ? "Aperçu prêt"
                                        : "Preview ready"}
                                    </strong>
                                  </div>
                                  <div>
                                    <span>{lang === "fr" ? "Forum" : "Forum"}</span>
                                    <strong>{forumDisplay}</strong>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div>
                                    <span>{t.created}</span>
                                    <strong>{createdDate}</strong>
                                  </div>
                                  <div>
                                    <span>{t.scenes}</span>
                                    <strong>{scenesCount}</strong>
                                  </div>
                                  <div>
                                    <span>{t.writingMode}</span>
                                    <strong>{modeDisplay}</strong>
                                  </div>
                                  <div>
                                    <span>{lang === "fr" ? "Visibilité" : "Visibility"}</span>
                                    <strong>{visibilityCopy.label}</strong>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="ns-storyhub-actionStrip">
                            <button
                              type="button"
                              className="ns-button ns-button-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                isPublishedBookCard ? handleOpenReader(story) : handleOpenWriterRoom(story);
                              }}
                            >
                              {isPublishedBookCard
                                ? lang === "fr"
                                  ? "Ouvrir l’aperçu lecteur"
                                  : "Open Reading Preview"
                                : t.enterWritersRoom}
                            </button>
                            {isPublishedBookCard && publicationInfo.amazonHref ? (
                              <button
                                type="button"
                                className="ns-button ns-button-outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenPublicationLink(story);
                                }}
                              >
                                {publicationInfo.cta}
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="ns-button ns-button-outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenReader(story);
                                }}
                              >
                                {t.openReaderViewCard}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : null}

                      {focused && storyHubTab === "details" ? (
                        <div className="ns-storyhub-tabPanel">
                          <div
                            className="ns-card"
                            style={{ padding: 16, marginTop: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 12,
                                marginBottom: 10,
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 800, color: "#0f172a" }}>
                                  {t.description}
                                </div>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                                  {t.max200Words}
                                </div>
                              </div>

                              {descriptionOpen ? (
                                <button
                                  className="ns-button ns-button-outline"
                                  disabled={countWords(descriptionDraft) > 200}
                                  title={
                                    countWords(descriptionDraft) > 200
                                      ? lang === "fr"
                                        ? "Raccourcissez la description à 200 mots ou moins."
                                        : "Shorten the description to 200 words or fewer."
                                      : undefined
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveDescriptionForStory(story);
                                  }}
                                >
                                  {t.save}
                                </button>
                              ) : (
                                <button
                                  className="ns-button"
                                  style={{
                                    background: "#f1f5f9",
                                    color: "#0f172a",
                                    border: "1px solid #e2e8f0",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDescriptionEditor(sid, story.description);
                                  }}
                                >
                                  {t.edit}
                                </button>
                              )}
                            </div>

                            {descriptionOpen ? (
                              <>
                                <textarea
                                  value={descriptionDraft}
                                  onChange={(e) => {
                                    setDescriptionDraft(e.target.value);
                                    setDescriptionSaveMsg("");
                                  }}
                                  placeholder={t.descriptionPlaceholderShort}
                                  rows={4}
                                  style={{
                                    width: "100%",
                                    borderRadius: 10,
                                    border: "1px solid #cbd5e1",
                                    padding: 10,
                                    outline: "none",
                                    resize: "vertical",
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div
                                  style={{
                                    marginTop: 8,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    gap: 10,
                                    flexWrap: "wrap",
                                    fontSize: 12,
                                  }}
                                >
                                  <span
                                    style={{
                                      color:
                                        countWords(descriptionDraft) > 200
                                          ? "#b91c1c"
                                          : "#64748b",
                                      fontWeight:
                                        countWords(descriptionDraft) > 200 ? 700 : 400,
                                    }}
                                  >
                                    {t.words}: {countWords(descriptionDraft)} / 200
                                  </span>
                                  {descriptionSaveMsg ? (
                                    <span style={{ color: "#64748b" }}>
                                      {descriptionSaveMsg}
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            ) : (
                              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, color: "#0f172a" }}>
                                {String(story.description || "").trim()
                                  ? String(story.description || "")
                                  : t.notProvidedYet}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}

                      {focused && storyHubTab === "author" ? (
                        <div className="ns-storyhub-tabPanel" onClick={(e) => e.stopPropagation()}>
                          <div className="ns-card ns-card-soft" style={{ marginTop: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "baseline",
                              }}
                            >
                              <h4 className="ns-card-title" style={{ margin: 0 }}>
                                {t.aboutAuthorTitle}
                              </h4>
                              {!aboutOpen ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openAboutEditor(story);
                                  }}
                                >
                                  {String(story.aboutAuthor || "").trim() ? t.edit : t.add}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeAboutEditor();
                                  }}
                                >
                                  {t.cancel}
                                </button>
                              )}
                            </div>

                            {!aboutOpen ? (
                              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                                {String(story.aboutAuthor || "").trim()
                                  ? shortSnippet(story.aboutAuthor, 420)
                                  : t.notProvidedYet}
                              </p>
                            ) : (
                              <>
                                <textarea
                                  rows={5}
                                  value={aboutDraft}
                                  onChange={(e) => {
                                    setAboutDraft(e.target.value);
                                    setAboutSaveMsg("");
                                  }}
                                  placeholder={lang === "fr" ? "Max 150 mots." : "Max 150 words."}
                                  style={{ width: "100%", marginTop: 8 }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: 8,
                                    gap: 10,
                                  }}
                                >
                                  <p className="ns-card-meta" style={{ margin: 0 }}>
                                    {aboutSaveMsg || `${t.words}: ${countWords(aboutDraft)} / 150`}
                                  </p>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      saveAboutForStory(story);
                                    }}
                                  >
                                    {t.saveAboutAuthor}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>

                          <div className="ns-card ns-card-soft" style={{ marginTop: 10 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "baseline",
                              }}
                            >
                              <h4 className="ns-card-title" style={{ margin: 0 }}>
                                {lang === "fr" ? "Synopsis" : "Synopsis"}
                              </h4>
                              {!synopsisOpen ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openSynopsisEditor(story);
                                  }}
                                >
                                  {String(story.synopsis || "").trim() ? t.edit : t.add}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeSynopsisEditor();
                                  }}
                                >
                                  {t.cancel}
                                </button>
                              )}
                            </div>

                            {!synopsisOpen ? (
                              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                                {String(story.synopsis || "").trim()
                                  ? shortSnippet(story.synopsis, 420)
                                  : t.notProvidedYet}
                              </p>
                            ) : (
                              <>
                                <textarea
                                  rows={6}
                                  value={synopsisDraft}
                                  onChange={(e) => {
                                    setSynopsisDraft(e.target.value);
                                    setSynopsisSaveMsg("");
                                  }}
                                  placeholder={lang === "fr" ? "Max 150 mots." : "Max 150 words."}
                                  style={{ width: "100%", marginTop: 8 }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: 8,
                                    gap: 10,
                                  }}
                                >
                                  <p className="ns-card-meta" style={{ margin: 0 }}>
                                    {synopsisSaveMsg || `${t.words}: ${countWords(synopsisDraft)} / 150`}
                                  </p>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      saveSynopsisForStory(story);
                                    }}
                                  >
                                    {t.saveSynopsis}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                          <StoryTeaserEditor
                            key={sid}
                            story={story}
                            language={lang}
                            onSave={async (nextText) => {
                              const result = await saveStoryMeta(sid, { storyTeaser: nextText });
                              if (result.ok) {
                                updateStoryInState(
                                  sid,
                                  result.story && typeof result.story === "object"
                                    ? result.story
                                    : { storyTeaser: nextText, updatedAt: new Date().toISOString() }
                                );
                              }
                              return result;
                            }}
                          />
                        </div>
                      ) : null}

                      {focused && storyHubTab === "images" ? (
                        <div className="ns-storyhub-tabPanel" onClick={(e) => e.stopPropagation()}>
                          <div className="ns-card ns-card-soft" style={{ marginTop: 0 }}>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                alignItems: "baseline",
                              }}
                            >
                              <h4 className="ns-card-title" style={{ margin: 0 }}>
                                {t.images}
                              </h4>
                              {!mediaOpen ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMediaEditor(story);
                                  }}
                                >
                                  {t.edit}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeMediaEditor();
                                  }}
                                >
                                  {t.cancel}
                                </button>
                              )}
                            </div>

                            {!mediaOpen ? (
                              <p className="ns-card-meta" style={{ marginTop: 8 }}>
                                {story.coverImageUrl ? t.coverSet : t.coverNotSet} ·{" "}
                                {story.authorPhotoUrl ? t.authorPhotoSet : t.authorPhotoNotSet}
                              </p>
                            ) : (
                              <>
                                <div className="ns-field" style={{ marginTop: 8 }}>
                                  <label>
                                    {t.authorPhotoUrl}
                                    <input
                                      type="text"
                                      value={mediaAuthorPhotoDraft}
                                      onChange={(e) => {
                                        setMediaAuthorPhotoDraft(e.target.value);
                                        setMediaSaveMsg("");
                                      }}
                                      placeholder={lang === "fr" ? "https://.../auteur.jpg" : "https://.../author.jpg"}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </label>
                                </div>

                                <div className="ns-field" style={{ marginTop: 8 }}>
                                  <label>
                                    {lang === "fr" ? "URL de l’image de couverture" : "Cover Image URL"}
                                    <input
                                      type="text"
                                      value={mediaCoverDraft}
                                      onChange={(e) => {
                                        setMediaCoverDraft(e.target.value);
                                        setMediaSaveMsg("");
                                        setMediaCoverUploadMessage("");
                                      }}
                                      placeholder="https://.../cover.jpg"
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </label>
                                </div>

                                <div className="ns-cover-upload-box" style={{ marginTop: 10 }}>
                                  <div className="ns-cover-upload-head">
                                    <strong>{t.coverUploadLabel}</strong>
                                    <span>{t.coverUploadHelp}</span>
                                  </div>
                                  <div className="ns-cover-upload-row">
                                    <input
                                      type="file"
                                      accept="image/jpeg,image/png,image/webp"
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        const file =
                                          e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                        setMediaCoverUploadFile(file);
                                        setMediaCoverUploadMessage("");
                                        setMediaSaveMsg("");
                                      }}
                                    />
                                    <button
                                      type="button"
                                      className="ns-button ns-button-outline"
                                      onClick={handleMediaCoverUpload}
                                      disabled={mediaCoverUploading || !mediaCoverUploadFile}
                                    >
                                      {mediaCoverUploading ? t.coverUploading : t.coverUploadButton}
                                    </button>
                                  </div>
                                  {mediaCoverUploadMessage ? (
                                    <p className="ns-card-meta ns-cover-upload-message">
                                      {mediaCoverUploadMessage}
                                    </p>
                                  ) : null}
                                </div>

                                <p className="ns-policy-microline" style={{ marginTop: 8 }}>
                                  {t.imageResponsibilityLine}{" "}
                                  <a href={t.illustrationPolicyHref} target="_blank" rel="noreferrer">
                                    {t.illustrationPolicyLabel}
                                  </a>
                                </p>

                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginTop: 8,
                                    gap: 10,
                                  }}
                                >
                                  <p className="ns-card-meta" style={{ margin: 0 }}>
                                    {mediaSaveMsg || t.useDirectImageLinks}
                                  </p>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      saveMediaForStory(story);
                                    }}
                                  >
                                    {t.saveImages}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ) : null}


                      {focused && storyHubTab === "publication" ? (
                        <div className="ns-storyhub-tabPanel" onClick={(e) => e.stopPropagation()}>
                          <div className="ns-card ns-card-soft ns-visibility-admin-panel" style={{ marginTop: 0, marginBottom: 12 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                              <div>
                                <h4 className="ns-card-title" style={{ margin: 0 }}>
                                  {lang === "fr" ? "Visibilité de lecture" : "Reading visibility"}
                                </h4>
                                <p className="ns-card-meta" style={{ marginTop: 6 }}>
                                  {isPublishedBookCard
                                    ? lang === "fr"
                                      ? "Ce livre est terminé ou publié. Reader View présente les informations finales du livre, et le lien de publication mène au livre complet."
                                      : "This book is completed or published. Reader View presents the final book details, and the publication link points to the complete book."
                                    : lang === "fr"
                                    ? "Choisissez qui peut lire cette storyline pendant qu’elle grandit."
                                    : "Choose who may read this storyline while it is still growing."}
                                </p>
                              </div>
                              {!visibilityOpen ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openVisibilityEditor(story);
                                  }}
                                >
                                  {lang === "fr" ? "Modifier la visibilité" : "Edit visibility"}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closeVisibilityEditor();
                                  }}
                                >
                                  {t.cancel}
                                </button>
                              )}
                            </div>

                            {!visibilityOpen ? (
                              <div className="ns-publication-summary-grid" style={{ marginTop: 14 }}>
                                <div>
                                  <span>{lang === "fr" ? "Visibilité" : "Visibility"}</span>
                                  <strong>{visibilityCopy.label}</strong>
                                </div>
                                <div>
                                  <span>{lang === "fr" ? "Règle" : "Rule"}</span>
                                  <strong>{visibilityCopy.help}</strong>
                                </div>
                              </div>
                            ) : (
                              <div className="ns-publication-form-grid" style={{ marginTop: 14 }}>
                                <label className="ns-publication-form-wide">
                                  <span>{lang === "fr" ? "Qui peut lire ?" : "Who can read?"}</span>
                                  <select
                                    value={visibilityDraft}
                                    onChange={(e) => {
                                      setVisibilityDraft(e.target.value);
                                      setVisibilitySaveMsg("");
                                    }}
                                  >
                                    {STORY_VISIBILITY_OPTIONS.map((option) => {
                                      const copy = getStoryVisibilityCopy(option, lang);
                                      return (
                                        <option key={option} value={option}>
                                          {copy.label}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </label>
                                <div className="ns-publication-form-wide">
                                  <p className="ns-card-meta" style={{ margin: 0 }}>
                                    {getStoryVisibilityCopy(visibilityDraft, lang).help}
                                  </p>
                                </div>
                                <div className="ns-publication-form-actions">
                                  <p className="ns-card-meta">{visibilitySaveMsg}</p>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      saveVisibilityForStory(story);
                                    }}
                                  >
                                    {lang === "fr" ? "Enregistrer la visibilité" : "Save visibility"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="ns-card ns-card-soft ns-publication-admin-panel" style={{ marginTop: 0 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                              <div>
                                <h4 className="ns-card-title" style={{ margin: 0 }}>
                                  {lang === "fr" ? "Statut de publication" : "Publication status"}
                                </h4>
                                <p className="ns-card-meta" style={{ marginTop: 6 }}>
                                  {lang === "fr"
                                    ? "Définissez ici l’identité finale et le statut officiel du livre. Les pages publiées utiliseront ces informations."
                                    : "Set the final book identity and official publication status here. Published pages will use these details."}
                                </p>
                              </div>
                              {!publicationOpen ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openPublicationEditor(story);
                                  }}
                                >
                                  {lang === "fr" ? "Modifier la publication" : "Edit publication"}
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    closePublicationEditor();
                                  }}
                                >
                                  {t.cancel}
                                </button>
                              )}
                            </div>

                            {!publicationOpen ? (
                              <div className="ns-publication-summary-grid" style={{ marginTop: 14 }}>
                                <div>
                                  <span>{lang === "fr" ? "Titre publié" : "Published title"}</span>
                                  <strong>{String(publicationInfo.publishedTitle || story.title || "").trim() || t.notProvidedYet}</strong>
                                </div>
                                <div>
                                  <span>{lang === "fr" ? "Sous-titre publié" : "Published subtitle"}</span>
                                  <strong>{String(publicationInfo.publishedSubtitle || "").trim() || t.notProvidedYet}</strong>
                                </div>
                                <div>
                                  <span>{lang === "fr" ? "Statut" : "Status"}</span>
                                  <strong>{publicationInfo.publicationLabel}</strong>
                                </div>
                                <div>
                                  <span>{lang === "fr" ? "Nom d’auteur publié" : "Published author name"}</span>
                                  <strong>{String(story.publishedAuthorName || story.authorName || author.display || "").trim() || t.notProvidedYet}</strong>
                                </div>
                                <div>
                                  <span>{lang === "fr" ? "Lien de publication" : "Publication link"}</span>
                                  <strong>{String(story.publicationUrl || "").trim() ? (lang === "fr" ? "Fourni" : "Provided") : t.notProvidedYet}</strong>
                                </div>
                                <div>
                                  <span>{lang === "fr" ? "Date" : "Date"}</span>
                                  <strong>{String(story.publishedAt || "").trim() || t.notProvidedYet}</strong>
                                </div>
                              </div>
                            ) : (
                              <div className="ns-publication-form-grid" style={{ marginTop: 14 }}>
                                <label className="ns-publication-form-wide">
                                  <span>{lang === "fr" ? "Titre final du livre" : "Final book title"}</span>
                                  <input
                                    value={publicationDraft.publicationTitle}
                                    maxLength={160}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publicationTitle: e.target.value }))
                                    }
                                    placeholder={String(story?.title || "").trim() || (lang === "fr" ? "Titre du livre" : "Book title")}
                                  />
                                </label>

                                <label className="ns-publication-form-wide">
                                  <span>{lang === "fr" ? "Sous-titre final" : "Final book subtitle"}</span>
                                  <input
                                    value={publicationDraft.publicationSubtitle}
                                    maxLength={240}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publicationSubtitle: e.target.value }))
                                    }
                                    placeholder={lang === "fr" ? "Facultatif" : "Optional"}
                                  />
                                </label>

                                <label>
                                  <span>{lang === "fr" ? "Statut" : "Status"}</span>
                                  <select
                                    value={publicationDraft.publicationStatus}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publicationStatus: e.target.value }))
                                    }
                                  >
                                    <option value="inDevelopment">{lang === "fr" ? "En développement" : "In development"}</option>
                                    <option value="completed">{lang === "fr" ? "Complété / poli" : "Completed / polished"}</option>
                                    <option value="publishedAmazon">{lang === "fr" ? "Publié sur Amazon" : "Published on Amazon"}</option>
                                    <option value="publishedExternal">{lang === "fr" ? "Publié ailleurs" : "Published elsewhere"}</option>
                                  </select>
                                </label>

                                <label>
                                  <span>{lang === "fr" ? "Nom d’auteur publié" : "Published author name"}</span>
                                  <input
                                    value={publicationDraft.publishedAuthorName}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publishedAuthorName: e.target.value }))
                                    }
                                    placeholder={lang === "fr" ? "Ex. Goddy Oguzie" : "Example: Goddy Oguzie"}
                                  />
                                </label>

                                <label>
                                  <span>{lang === "fr" ? "Lien Amazon / publication" : "Amazon / publication link"}</span>
                                  <input
                                    value={publicationDraft.publicationUrl}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publicationUrl: e.target.value }))
                                    }
                                    placeholder="https://..."
                                  />
                                </label>

                                <label>
                                  <span>{lang === "fr" ? "Couverture finale publiée" : "Final published cover URL"}</span>
                                  <input
                                    value={publicationDraft.publicationCoverUrl}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publicationCoverUrl: e.target.value }))
                                    }
                                    placeholder="https://..."
                                  />
                                </label>

                                <label>
                                  <span>{lang === "fr" ? "Date de publication" : "Published date"}</span>
                                  <input
                                    value={publicationDraft.publishedAt}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publishedAt: e.target.value }))
                                    }
                                    placeholder="YYYY-MM-DD"
                                  />
                                </label>

                                <label className="ns-publication-form-wide">
                                  <span>{lang === "fr" ? "Note interne courte" : "Short internal note"}</span>
                                  <textarea
                                    rows={3}
                                    value={publicationDraft.publicationNote}
                                    onChange={(e) =>
                                      setPublicationDraft((prev) => ({ ...prev, publicationNote: e.target.value }))
                                    }
                                    placeholder={
                                      lang === "fr"
                                        ? "Optionnel : note courte sur cette publication."
                                        : "Optional: short note about this publication."
                                    }
                                  />
                                </label>

                                <div className="ns-publication-form-actions">
                                  <p className="ns-card-meta">{publicationSaveMsg}</p>
                                  <button
                                    type="button"
                                    className="ns-button ns-button-primary"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      savePublicationForStory(story);
                                    }}
                                  >
                                    {lang === "fr" ? "Enregistrer la publication" : "Save publication settings"}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}

                      {focused && storyHubTab === "actions" ? (
                        <div className="ns-storyhub-tabPanel" onClick={(e) => e.stopPropagation()}>
                          <div className="ns-storyhub-actionPanel">
                            <div>
                              <h4 className="ns-card-title" style={{ margin: 0 }}>
                                {lang === "fr" ? "Où aller ensuite" : "Where to go next"}
                              </h4>
                              <p className="ns-card-meta" style={{ marginTop: 6 }}>
                                {isPublishedBookCard
                                  ? lang === "fr"
                                    ? "Ce livre a quitté l’atelier. Ouvrez sa page lecteur ou allez vers la page de publication."
                                    : "This book has left the workshop. Open its reader page or go to the publication page."
                                  : lang === "fr"
                                  ? "Ouvrez la salle d’écriture pour gérer le canon, ou Reader View pour voir l’histoire comme un lecteur."
                                  : "Open the Writer’s Room to manage the canon, or Reader View to see the story as a reader."}
                              </p>
                            </div>

                            <div className="ns-storyhub-actionStrip">
                              {!isPublishedBookCard ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenWriterRoom(story);
                                  }}
                                >
                                  {t.enterWritersRoom}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className={isPublishedBookCard ? "ns-button ns-button-primary" : "ns-button ns-button-outline"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenReader(story);
                                }}
                              >
                                {isPublishedBookCard
                                  ? lang === "fr"
                                    ? "Ouvrir la page du livre"
                                    : "Open Book Page"
                                  : t.openReaderViewCard}
                              </button>
                              {isPublishedBookCard && publicationInfo.amazonHref ? (
                                <button
                                  type="button"
                                  className="ns-button ns-button-outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenPublicationLink(story);
                                  }}
                                >
                                  {publicationInfo.cta}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            )}

            {!loading && myStories.length > 0 && (
              <div className="ns-storyhub-afterShelf">
                <div className="ns-storyhub-scrollControls" aria-label={lang === "fr" ? "Faire défiler les storylines" : "Scroll storylines"}>
                  <button
                    type="button"
                    className="ns-button ns-button-outline ns-storyhub-scrollBtn"
                    onClick={() => {
                      try {
                        document.querySelector(".ns-storyhub-boardList")?.scrollBy({ left: -560, behavior: "smooth" });
                      } catch {}
                    }}
                  >
                    ← {lang === "fr" ? "Précédent" : "Previous"}
                  </button>
                  <button
                    type="button"
                    className="ns-button ns-button-outline ns-storyhub-scrollBtn"
                    onClick={() => {
                      try {
                        document.querySelector(".ns-storyhub-boardList")?.scrollBy({ left: 560, behavior: "smooth" });
                      } catch {}
                    }}
                  >
                    {lang === "fr" ? "Suivant" : "Next"} →
                  </button>
                </div>

                <div className="ns-storyhub-afterActions">
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() => (onNavigatePage ? onNavigatePage("writerStudio") : null)}
                  >
                    {t.openWriterStudio}
                  </button>
                  <button
                    type="button"
                    className="ns-button ns-button-outline"
                    onClick={() => (onNavigatePage ? onNavigatePage("readerView") : null)}
                  >
                    {t.openReaderView}
                  </button>
                </div>

                <div className="ns-storyhub-guidanceShelf" aria-label={lang === "fr" ? "Aide et règles" : "Guidance and rules"}>
                  <details className="ns-storyhub-guidanceDetails">
                    <summary>{lang === "fr" ? "Contrôle de l’auteur et règles Canon →" : "Writer control and Canon rules →"}</summary>
                    <div className="ns-trust-notice ns-trust-notice-compact" role="note">
                      <div className="ns-trust-notice-icon" aria-hidden="true">✓</div>
                      <div>
                        <div className="ns-trust-notice-title">{t.storyControlNoticeTitle}</div>
                        <p className="ns-trust-notice-body">{t.storyControlNoticeBody}</p>
                      </div>
                    </div>
                  </details>

                  <details className="ns-storyhub-guidanceDetails">
                    <summary>{t.policyLinksTitle} →</summary>
                    <PolicyLinksCard
                      lang={lang}
                      title={t.policyLinksTitle}
                      body={t.policyLinksBody}
                      compact
                    />
                  </details>

                  <details className="ns-storyhub-guidanceDetails">
                    <summary>{t.foundingWritersPilotTitle} →</summary>
                    <FoundingWritersPilotCard
                      lang={lang}
                      title={t.foundingWritersPilotTitle}
                      body={t.foundingWritersPilotBody}
                      linkLabel={t.foundingWritersPilotLink}
                    />
                  </details>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
